import { safeUpdateDoc, safeAddDoc, getAbsoluteTimeMs } from '../utils';
import { useMemo, useEffect, useLayoutEffect, useRef, useState, memo, useCallback, Fragment } from 'react';
import { usePersistentState } from '../hooks/usePersistentState';
import { ChevronLeft, ChevronRight, Check, CheckSquare, Star, Plus, X, Plane, PlaneTakeoff, Thermometer, Calendar, ListTodo, Ticket, Utensils, Binoculars, Sun, Cloud, CloudSun, CloudRain, CloudDrizzle, CloudLightning, CloudSnow, Haze, Rows, Grid3x3, Cake, PartyPopper, Radar, HelpCircle, Map as MapIcon, Repeat, Bed, MapPin, Martini, Coffee, BookmarkPlus, Music, Database, AlignJustify, Menu, ShoppingBag } from 'lucide-react';
import { useTags } from '../hooks/useTags';
import { useRecommendations } from '../hooks/useRecommendations';
import { useProject } from '../contexts/ProjectDataContext';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import CalendarMapPanel from './CalendarMapPanel';
import MapSearchBox from './MapSearchBox';
import ConcertDetailsOverlay from './ConcertDetailsOverlay';
import TagNode from './TagNode';
import DatabaseView from './DatabaseView';
import { getActiveLocationsForDate } from '../utils';
import { auth } from '../firebase';

let globalCalendarMapState = {
  mapStartDate: undefined,
  mapEndDate: undefined,
  searchRadius: undefined,
  customCenter: undefined
};

const normalizeDate = (dateVal) => {
  if (!dateVal) return null;
  try {
    let d;
    if (dateVal.seconds) d = new Date(dateVal.seconds * 1000);
    else d = new Date(dateVal);

    if (isNaN(d.getTime())) return null;
    // Use local date to avoid timezone shifts to the next day
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    return null;
  }
};

const getTempColor = (temp) => {
  if (temp === undefined || temp === null) return 'inherit';
  if (temp < 32) return '#6D28D9'; // Deep Purple
  if (temp <= 50) return '#2563EB'; // Royal Blue
  if (temp <= 65) return '#0D9488'; // Deep Teal
  if (temp <= 79) return '#CA8A04'; // Dark Gold
  if (temp <= 94) return '#EA580C'; // Burnt Orange
  return '#DC2626'; // Crimson Red
};

const WeatherIcon = ({ code, size = 16 }) => {
  const props = {
    size,
    strokeWidth: 2.5,
    className: 'text-zinc-700'
  };

  if (code === 0 || code === 1) return <Sun {...props} fill="currentColor" />;
  if (code === 2) return <CloudSun {...props} fill="currentColor" />;
  if (code === 3) return <Cloud {...props} fill="currentColor" />;
  if (code === 45 || code === 48) return <Haze {...props} />;
  if (code >= 51 && code <= 57) return <CloudDrizzle {...props} fill="currentColor" />;
  if (code >= 61 && code <= 67) return <CloudRain {...props} fill="currentColor" />;
  if (code >= 80 && code <= 82) return <CloudRain {...props} fill="currentColor" />;
  if (code >= 71 && code <= 77) return <CloudSnow {...props} fill="currentColor" />;
  if (code >= 85 && code <= 86) return <CloudSnow {...props} fill="currentColor" />;
  if (code >= 95) return <CloudLightning {...props} fill="currentColor" />;
  return <Thermometer {...props} />;
};

const DailyWeather = ({ weather, fontSize = 11 }) => {
  if (!weather || weather.max === undefined || weather.min === undefined) return null;

  return (
    <div className="flex flex-col select-none" style={{ maxWidth: '167px' }}>
      <div className="flex items-center leading-none font-bold tabular-nums h-full">
        <span style={{ color: getTempColor(weather.max), fontSize: `${fontSize}px` }}>
          {weather.max}°
        </span>
        <span className="text-zinc-400 font-normal px-[2px]" style={{ fontSize: `${fontSize * 0.8}px` }}>
          /
        </span>
        <span className="text-zinc-500 font-normal" style={{ fontSize: `${fontSize * 0.8}px` }}>
          {weather.min}°
        </span>
        <div style={{ width: '8px' }} />
        <WeatherIcon code={weather.code} size={fontSize + 3} />
      </div>
      {weather.precip !== undefined && (
        <div className="w-full mt-[1px] bg-zinc-200 h-[2px] rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-500 transition-all duration-500" 
            style={{ width: `${weather.precip}%` }} 
          />
        </div>
      )}
    </div>
  );
};

const getCalendarMonths = (baseDate, monthsBack = 2, monthsForward = 36, mondayFirst = false) => {
  const months = [];
  const startMonth = new Date(baseDate.getFullYear(), baseDate.getMonth() - monthsBack, 1);
  for (let i = 0; i <= monthsBack + monthsForward; i++) {
    const monthDate = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);
    const year = monthDate.getFullYear();
    const monthIndex = monthDate.getMonth();
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    const weeks = [];
    const firstDayOfMonth = new Date(year, monthIndex, 1);
    const lastDayOfMonth = new Date(year, monthIndex + 1, 0);
    let currentDay = new Date(firstDayOfMonth);
    
    let startOffset = currentDay.getDay();
    if (mondayFirst) {
      startOffset = startOffset === 0 ? 6 : startOffset - 1;
    }
    currentDay.setDate(currentDay.getDate() - startOffset);
    
    while (currentDay <= lastDayOfMonth) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        week.push(new Date(currentDay));
        currentDay.setDate(currentDay.getDate() + 1);
      }
      weeks.push(week);
    }
    months.push({
      monthKey,
      year,
      monthIndex,
      weeks,
      label: monthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    });
  }
  return months;
};

const MONTH_COLORS = [
  'bg-blue-100/80',    // Jan
  'bg-sky-50/80',      // Feb
  'bg-teal-50/80',     // Mar
  'bg-green-50/80',    // Apr
  'bg-lime-50/80',     // May
  'bg-yellow-50/80',   // Jun
  'bg-orange-50/80',   // Jul
  'bg-red-50/80',      // Aug
  'bg-amber-50/80',    // Sep
  'bg-orange-100/80',  // Oct
  'bg-stone-100/80',   // Nov
  'bg-slate-100/80',   // Dec
];

const formatTimeForTz = (date, timezone, homeTz) => {
  try {
    const targetTz = timezone === 'home' || !timezone ? homeTz : timezone;
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: targetTz || undefined,
    }).format(date).replace(/\s/g, '').toLowerCase().replace(/m/g, '').replace(/:00(?=[ap])/g, '');
  } catch (e) {
    return '';
  }
};

const formatTzAbbr = (date, timezone, homeTz) => {
  try {
    const targetTz = timezone === 'home' || !timezone ? homeTz : timezone;
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
      timeZone: targetTz || undefined,
    }).formatToParts(date);
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch (e) {
    return '';
  }
};

const DraggableEvent = ({
  m,
  dayId,
  isTravelDay,
  navigate,
  dispatch,
  setSelectedFlight,
  getItemColor,
  toggleTask,
  hoveredEventId,
  setHoveredEventId,
  fontSize,
  isCollapsed,
  showEventLocations,
  setSelectedTripId,
  isCalendarBold,
  firstLocCity,
  lastLocCity,
  isSegmented,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: m.item.id,
    data: { 
      type: m.item.type, 
      item: m.item,
      task: m.item.type === 'task' ? m.item : null,
      draggedFromDayId: dayId
    },
  });

  const isHovered = hoveredEventId === m.item.id;
  const isStart = !isSegmented || m.item.startDate === dayId;
  const isEnd = !isSegmented || m.item.endDate === dayId;

  const isHeader = m.item.type === 'task' && m.item.isHeader;
  const isChild = false; // Folders are deprecated, enforce top-level styling for all tasks
  const isProjectStarred = false;

  const roundedClass =
    isStart && isEnd
      ? 'rounded-md'
      : isStart
        ? 'rounded-l-md shadow-[2px_2px_4px_rgba(0,0,0,0.1)]'
        : isEnd
          ? 'rounded-r-md shadow-[-2px_2px_4px_rgba(0,0,0,0.1)]'
          : 'rounded-none';

  // Only show details on the absolute first day of the event (unless !isSegmented, then show on all days)
  const showDetails = !isSegmented || m.item.startDate === dayId;

  // Scale box height based on font size (e.g., 9px font -> 18px height, 12px font -> 24px height)
  const boxHeight = fontSize + 9;
  const verticalGap = 1.5; // Fixed gap between items

  if (m.item.type === 'tz-header') {
    if (!isTravelDay) return null;
    return (
      <div
        data-testid={`calendar-item-${m.item.id}`}
        style={{
          position: 'relative',
          marginLeft: isStart ? '2px' : '0px',
          marginRight: isEnd ? '2px' : '-1px',
          height: `${boxHeight}px`,
        }}
        className="flex-shrink-0 flex items-end pb-1 pointer-events-none border-b border-zinc-300 border-dashed opacity-70 z-20"
      >
        {showDetails && (
          <span
            style={{ fontSize: `${fontSize - 1}px` }}
            className="font-black text-zinc-500 uppercase tracking-widest pl-1 bg-zinc-200 rounded-full px-2 shadow-sm mb-[-6px]"
          >
            {m.item.title}{m.item.type === 'tentative' ? '?' : ''}

          </span>        )}
      </div>
    );
  }

  const isNonAllDayEvent = !m.item.isAllDay && m.item.startTime && m.item.type !== 'task';

  let taskBgColor = 'rgba(34, 197, 94, 0.05)';
  if (isProjectStarred) {
    taskBgColor = '#eab308'; // yellow-500
  } else if (m.item.type === 'task' && !isHeader) {
    if (m.item.priority === 'High') taskBgColor = 'rgba(34, 197, 94, 0.3)';
    else if (m.item.priority === 'Medium') taskBgColor = 'rgba(34, 197, 94, 0.15)';
    else taskBgColor = 'rgba(34, 197, 94, 0.05)';
  }  const baseBgColor =
    m.item.type === 'task'
      ? isHeader
        ? 'transparent'
        : taskBgColor
      : isNonAllDayEvent
        ? 'transparent'
        : m.item.type === 'flight'
          ? '#0ea5e9'
          : getItemColor(m.item);

  let bgStyle = { backgroundColor: baseBgColor };
  if (m.item.multiTypes && m.item.multiTypes.length > 1) {
    const stops = [];
    const len = m.item.multiTypes.length;
    m.item.multiTypes.forEach((t, i) => {
      const color = getItemColor({ type: t });
      const start = (i / len) * 100;
      const end = ((i + 1) / len) * 100;
      stops.push(`${color} ${start}%`);
      stops.push(`${color} ${end}%`);
    });
    bgStyle = { background: `linear-gradient(to right, ${stops.join(', ')})` };
  }

  const textColor = (m.item.type === 'task' || isNonAllDayEvent) ? (isProjectStarred ? 'text-white' : 'text-zinc-900') : 'text-white';

  const style = {
    position: 'relative',
    marginLeft: isStart ? '2px' : '-1px', // Seamless connection
    marginRight: isEnd ? '2px' : '-1px', // Seamless connection
    paddingLeft: isStart ? '0.25rem' : '0px',
    height: `${boxHeight}px`,
    ...bgStyle,
    cursor: isHeader ? 'default' : (isChild ? 'pointer' : 'pointer'),
    opacity: (m.item.completed ? 0.5 : 1) * (isDragging ? 0.3 : 1),
    transform: CSS.Translate.toString(transform),
    zIndex: isDragging ? 100 : isHovered ? 60 : 50,
    filter: isHovered ? 'brightness(1.1)' : 'none',
    fontSize: `${fontSize}px`,
    fontStyle: m.item.type === 'tentative' ? 'italic' : 'normal',
  };

  const isDraggable =
    ['event', 'concert', 'restaurant', 'drinks', 'coffee', 'sight', 'holiday', 'tentative', 'task', 'wanttogo'].includes(m.item.type) &&
    !m.item.isReadOnly && m.item.type !== 'flight';

  // Scale checkbox size with font size
  const checkSize = Math.max(8, fontSize - 2);

  const isBirthday = m.item.type === 'birthday';
  const isFlight = m.item.type === 'flight';
  const isHotel = m.item.type === 'hotel';

  return (
    <div
      ref={setNodeRef}
      data-testid={`calendar-item-${m.item.id}`}
      title={isBirthday ? "Click to edit this person's info for all years" : (isFlight || isHotel) ? "Click to edit trip" : undefined}
      {...(isDraggable ? listeners : {})}
      {...(isDraggable ? attributes : {})}
      onMouseEnter={() => setHoveredEventId(m.item.id)}
      onMouseLeave={() => setHoveredEventId(null)}
      onClick={(e) => {
        if (e.shiftKey && m.item.type === 'trip') {
          e.stopPropagation();
          e.preventDefault();
          if (setSelectedTripId) setSelectedTripId(m.item.id);
          return;
        }
        if (isDragging) return;
        e.stopPropagation();
        if (isHeader) {
          dispatch({ type: 'TOGGLE_COLLAPSE_HEADER', payload: m.item.id });
          return;
        }
        if (m.item.type === 'project') navigate(`/p/${m.item.id}` + window.location.search);
        else if (m.item.type === 'concert' && m.item.isRecommendation) {
          const evt = new CustomEvent('OPEN_CONCERT_DETAILS', { detail: { concert: m.item.data } });
          window.dispatchEvent(evt);
        } else if (m.item.isRecommendation && m.item.data?.placeId) {
          const evt = new CustomEvent('OPEN_PLACE_DETAILS', { detail: { placeId: m.item.data.placeId } });
          window.dispatchEvent(evt);
        } else if (m.item.type === 'task' && m.item.isProjectTask) {
          navigate(`/p/${m.item.channelId}` + window.location.search);
        } else if (['event', 'google-event', 'concert', 'restaurant', 'drinks', 'coffee', 'sight', 'holiday', 'birthday', 'tentative', 'task', 'flight', 'hotel', 'wanttogo'].includes(m.item.type)) {
          if (!m.item.isReadOnly || m.item.type === 'google-event') {
            if (isFlight) {
              setSelectedFlight(m.item);
            } else if (isHotel) {
              dispatch({ type: 'OPEN_EDIT_EVENT', payload: m.item.trip });
            } else {
              dispatch({ type: 'OPEN_EDIT_EVENT', payload: m.item });
            }
          }
        }
      }}
      onDoubleClick={(e) => {
        if (m.item.type === 'task') {
          e.stopPropagation();
          const channelId = m.item.channelId;
          if (channelId?.startsWith('personal-')) navigate('/home' + window.location.search);
          else navigate(`/p/${channelId}/tasks` + window.location.search);
        }
      }}
      style={style}
      className={`flex items-center px-1 cursor-pointer select-none truncate tracking-tight overflow-hidden ${m.item.isWantToGo ? 'border-emerald-500 border-[3px]' : 'border-zinc-200/30'} pointer-events-auto active:scale-[0.98] transition-all z-10 w-auto flex-shrink-0 ${textColor} ${roundedClass} ${m.item.isHeader ? 'opacity-80' : ''} ${isDragging ? 'rotate-2 shadow-xl' : ''} ${m.item.isWantToGo ? '' : (isStart && isEnd ? 'shadow-sm border' : isStart ? 'border-y border-l' : isEnd ? 'border-y border-r' : 'border-y')} ${isHeader ? 'font-black' : isCalendarBold ? 'font-black' : 'font-medium'} ${!isHeader && !isChild && (m.item.type === 'holiday' || m.item.type === 'tz-header') ? 'uppercase' : ''} ${isProjectStarred ? 'bg-yellow-500' : ''}`}
    >
      {showDetails && ((m.item.type === 'tentative' && !m.item.isReadOnly) || m.item.isRecommendation) && (
        <button
          onClick={async (e) => {
            e.stopPropagation();
            if (['restaurant', 'drinks', 'coffee', 'sight', 'wanttogo', 'concert', 'musicvenue'].includes(m.item.type)) {
              // Convert recommendation to a tentative event and open edit card
              const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
              const { db, auth } = await import('../firebase');
              const { appId: APP_ID_CONSTANT } = await import('../constants');
              const city = m.item.city || '';
              const isConcert = m.item.type === 'concert';
              const title = isConcert ? (m.item.data?.title || m.item.data?.artist || m.item.title) : (m.item.title || m.item.data?.placeName);
              const note = isConcert ? `Artist: ${m.item.data?.artist || 'Unknown'}\nVenue: ${m.item.data?.venue || 'Unknown'}\nLink: ${m.item.data?.songkickUrl || ''}` : '';
              
              const docRef = await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events'), {
                type: 'tentative',
                title: title,
                startDate: m.item.startDate,
                endDate: m.item.endDate,
                isAllDay: true,
                createdAt: serverTimestamp(),
                createdBy: auth.currentUser?.uid || 'test-uid-123',
                city: city,
                placeId: m.item.data?.placeId || null,
                lat: m.item.data?.lat || null,
                lng: m.item.data?.lng || null,
                note: note
              });
              
              dispatch({ 
                type: 'OPEN_EDIT_EVENT', 
                payload: { 
                  id: docRef.id, 
                  type: 'tentative', 
                  title: title,
                  startDate: m.item.startDate,
                  endDate: m.item.endDate,
                  venueName: m.item.data?.placeName || (isConcert ? m.item.data?.venue : m.item.title) || '',
                  address: m.item.data?.address || (isConcert ? m.item.data?.venue : '') || '',
                  addressLat: m.item.data?.lat || m.item.lat || null,
                  addressLng: m.item.data?.lng || m.item.lng || null,
                  city: city,
                  note: note
                } 
              });
            } else {
              // Quick convert tentative to event
              const { doc, updateDoc } = await import('firebase/firestore');
              const { db } = await import('../firebase');
              const { appId: APP_ID_CONSTANT } = await import('../constants');
              await safeUpdateDoc(doc(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events', m.item.id), {
                type: 'event',
                updatedAt: new Date()
              });
            }
          }}
          className="mr-1 p-0.5 hover:bg-white/20 rounded transition-colors"
          title={['restaurant', 'drinks', 'coffee', 'sight', 'wanttogo', 'concert', 'musicvenue'].includes(m.item.type) ? "Add to Tentative Plans" : "Convert to Event"}
        >
          <Plus size={checkSize} strokeWidth={4} />
        </button>
      )}
      {showDetails && m.item.type === 'task' && !isHeader && (
        <div
          data-testid={`calendar-task-checkbox-${m.item.id}`}
          style={{ width: `${checkSize}px`, height: `${checkSize}px` }}
          className={`rounded-sm border border-zinc-400 mr-1 flex-shrink-0 flex items-center justify-center transition-colors pointer-events-auto bg-white hover:bg-zinc-100`}
          onClick={(e) => {
            e.stopPropagation();
            toggleTask(m.item.originalId || m.item.id, m.item.completed);
          }}
        >
          {m.item.completed && (
            <Check size={checkSize - 2} strokeWidth={4} className="text-white" />
          )}
        </div>
      )}
      {showDetails && isHeader && (
        <div
          className={`mr-1 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
        >
          <ChevronRight size={10} strokeWidth={4} className="text-zinc-400" />
        </div>
      )}
      {showDetails && !m.item.isAllDay && m.item.startTime && (
        <>
          {isNonAllDayEvent && (
            <div 
              className="w-1.5 h-1.5 rounded-full mr-1 flex-shrink-0" 
              style={{ backgroundColor: getItemColor(m.item) }} 
            />
          )}
          <span className="opacity-70 mr-1 tabular-nums flex-shrink-0 lowercase">
            {new Date('2000-01-01T' + m.item.startTime)
              .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
              .replace(/\s/g, '').toLowerCase().replace(/m/g, '').replace(/:00(?=[ap])/g, '')}
          </span>
        </>
      )}
      {showDetails && (
        <span
          className={`truncate ${m.item.completed ? 'line-through opacity-70' : ''} ${isChild ? 'pl-1 opacity-80' : ''}`}
        >
          {m.item.isStarred && <Star className="inline mr-1 mb-0.5 text-amber-400 fill-amber-400 drop-shadow-sm" size={checkSize + 1} />}
          {isFlight && <span className="mr-1">{m.item.isTakeoff ? '🛫' : m.item.isLanding ? '🛬' : '✈️'}</span>}
          {isHotel && <Bed size={checkSize} className="inline mr-1 mb-0.5" />}
          {isFlight && m.item.isUpdated && <span className="font-bold text-red-500 mr-1">UPDATE:</span>}
          {(() => {
            const isTargetType = m.item.type === 'event' || m.item.type === 'tentative';
            let placeName = m.item.venueName || m.item.city || m.item.address || '';
            const suffix = m.item.type === 'tentative' ? '?' : '';
            
            if (isTargetType) {
              // Hide city tag if it's just the city and it matches the day's location
              const isJustCity = placeName === m.item.city;
              const matchesCurrentLocation = m.item.city === firstLocCity || m.item.city === lastLocCity;
              
              if (isJustCity && matchesCurrentLocation) {
                placeName = '';
              } else if (isJustCity && m.item.timezone) {
                // Add timezone abbreviation for city tags that are NOT in the current location
                const tzAbbr = formatTzAbbr(new Date(), m.item.timezone);
                if (tzAbbr) {
                  placeName = `${placeName.split(',')[0]} (${tzAbbr})`; // Use just the city name part for brevity
                }
              }

              if (!m.item.title) {
                return placeName ? `${placeName}${suffix}` : suffix;
              } else {
                const locationSuffix = showEventLocations && placeName ? ` @${placeName}` : '';
                return `${m.item.title}${locationSuffix}${suffix}`;
              }
            }
            return `${m.item.title}${suffix}`;
          })()}

          {isFlight && m.item.isTakeoff && m.item.startTime && (
            <span className="opacity-70 ml-1 tabular-nums">
              ({new Date(m.item.startTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '').toLowerCase().replace(/m/g, '').replace(/:00(?=[ap])/g, '')}{m.item.durationStr && ` • ${m.item.durationStr}`})
            </span>
          )}
          {isFlight && m.item.isLanding && m.item.startTime && (
            <span className="opacity-70 ml-1 tabular-nums">
              ({new Date(m.item.startTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '').toLowerCase().replace(/m/g, '').replace(/:00(?=[ap])/g, '')})
            </span>
          )}
          {isFlight && !m.item.isTakeoff && !m.item.isLanding && m.item.departureTime && (
            <span className="opacity-70 ml-1 tabular-nums">
              ({new Date(m.item.departureTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '').toLowerCase().replace(/m/g, '').replace(/:00(?=[ap])/g, '')}
              {m.item.arrivalTime && ` - ${new Date(m.item.arrivalTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '').toLowerCase().replace(/m/g, '').replace(/:00(?=[ap])/g, '')}`})
            </span>
          )}
          {isHotel && m.item.checkInTime && (
            <span className="opacity-70 ml-1 tabular-nums">
              ({new Date('2000-01-01T' + m.item.checkInTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '').toLowerCase().replace(/m/g, '').replace(/:00(?=[ap])/g, '')}
              {m.item.checkOutTime && ` - ${new Date('2000-01-01T' + m.item.checkOutTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '').toLowerCase().replace(/m/g, '').replace(/:00(?=[ap])/g, '')}`})
            </span>
          )}
        </span>
      )}    </div>
  );
};

const FullWeatherDisplay = ({ weather, city, fontSize }) => {
  if (!weather) return null;

  const getAQIStatus = (aqi) => {
    if (aqi === null) return null;
    if (aqi <= 50) return { label: 'Good', color: 'text-green-600', icon: '🟢' };
    if (aqi <= 100) return { label: 'Moderate', color: 'text-yellow-600', icon: '🟡' };
    if (aqi <= 150) return { label: 'Unhealthy for Sensitive', color: 'text-orange-600', icon: '🟠' };
    return { label: 'Unhealthy', color: 'text-red-600', icon: '🔴' };
  };

  const aqi = getAQIStatus(weather.aqi);

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    return new Date(isoStr).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/\s/g, '').toLowerCase().replace(/m/g, '').replace(/:00(?=[ap])/g, '');
  };

  const getWindArrow = (deg) => {
    if (deg === null || deg === undefined) return '';
    const arrows = ['↓', '↙', '←', '↖', '↑', '↗', '→', '↘'];
    return arrows[Math.round(deg / 45) % 8];
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  };

  return (
    <div className="flex flex-col gap-1 p-2 bg-zinc-50 border border-zinc-200 rounded shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-black uppercase text-zinc-900 tracking-tighter truncate max-w-[80px]">
          {city?.split(',')[0]}
        </span>
        <WeatherIcon code={weather.code} size={14} />
      </div>
      
      <div className="flex flex-col gap-0.5 mt-1">
        <div className="flex items-center gap-1.5 font-black tabular-nums tracking-tighter" style={{ fontSize: `${fontSize + 1}px` }}>
          <span style={{ color: getTempColor(weather.max) }}>{weather.max}°</span>
          <span className="text-zinc-300">/</span>
          <span style={{ color: getTempColor(weather.min) }}>{weather.min}°</span>
        </div>
        {(weather.apparentMax !== undefined && weather.apparentMin !== undefined) && (
          <div className="text-[9px] font-bold text-zinc-400 uppercase tracking-tighter">
            Feels like <span style={{ color: getTempColor(weather.apparentMax) }}>{weather.apparentMax}°</span> / <span style={{ color: getTempColor(weather.apparentMin) }}>{weather.apparentMin}°</span>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 gap-0.5 text-[9px] font-bold text-zinc-500 uppercase tracking-tighter mt-1">
        {(weather.precipSum > 0 || weather.snowSum > 0) ? (
          <div className="flex items-center gap-1 text-blue-600">
            <span>{weather.snowSum > 0 ? '❄️' : '🌧️'}</span>
            <span>{weather.precip}%</span>
            <span>({(weather.precipSum || weather.snowSum).toFixed(2)}in / {weather.precipHours}h)</span>
          </div>
        ) : (weather.precip !== undefined) ? (
          <div className="flex items-center gap-1">
            <span>🌧️</span>
            <span>{weather.precip}%</span>
          </div>
        ) : null}
        
        {weather.wind !== undefined && (
          <div className="flex items-center gap-1">
            <span>💨</span>
            <span>{weather.wind}mph {getWindArrow(weather.windDir)}</span>
          </div>
        )}

        {weather.uv !== null && weather.uv !== undefined && (
          <div className="flex items-center gap-1">
            <span>☀️</span>
            <span>UV: {Math.round(weather.uv)}</span>
          </div>
        )}

        {aqi && (
          <div className={`flex items-center gap-1 ${aqi.color}`}>
            <span>{aqi.icon}</span>
            <span>AQI: {aqi.label}</span>
          </div>
        )}

        {(weather.sunrise || weather.sunset) && (
          <div className="flex flex-col gap-0.5 mt-0.5 text-zinc-400">
            <div className="flex items-center justify-between">
              <span>🌅 {formatTime(weather.sunrise)}</span>
              <span>🌇 {formatTime(weather.sunset)}</span>
            </div>
            {weather.daylightDuration && (
              <span className="text-[8px] text-zinc-400 ml-0.5">☀️ {formatDuration(weather.daylightDuration)} of daylight</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const HabitTracker = ({ dayId, monthKey, habits, habitLogs, handleLogHabit, fontSize, dispatch, authUser }) => {
  const uid = authUser?.uid || 'test-uid-123';
  const docId = `${uid}_${monthKey}`;
  const logDoc = habitLogs.find(l => l.id === docId);
  const dayLog = logDoc?.days?.[dayId] || {};

  return (
    <div className="flex flex-col gap-0.5 mt-1 pt-1 border-t border-zinc-100">
      {habits
        .filter((habit) => {
          const start = habit.startDate;
          const end = habit.endDate || '9999-12-31';
          return dayId >= start && dayId <= end;
        })
        .map((habit) => {
          const entry = dayLog[habit.id] || {};
          return (
            <div key={habit.id} className="flex flex-col gap-0.5 group/habit">
              <div className="flex items-center gap-1.5 min-w-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLogHabit(monthKey, dayId, habit.id, { ...entry, completed: !entry.completed }, true);
                  }}
                  className={`shrink-0 transition-colors ${entry.completed ? 'text-green-600' : 'text-zinc-300 hover:text-zinc-500'}`}
                >
                  {entry.completed ? (
                    <CheckSquare size={fontSize + 2} />
                  ) : (
                    <div
                      className="border-2 border-current rounded-sm"
                      style={{ width: fontSize, height: fontSize }}
                    />
                  )}
                </button>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({ type: 'OPEN_EDIT_EVENT', payload: habit });
                  }}
                  className={`truncate text-zinc-900 font-bold leading-none cursor-pointer hover:underline ${entry.completed ? 'line-through opacity-50' : ''}`}
                  style={{ fontSize: `${fontSize}px` }}
                >
                  {habit.title}
                </span>
              </div>
            </div>
          );
        })}
    </div>
  );
};

const formatTime = (isoString) => {
  if (!isoString) return '--';
  return new Date(isoString).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s/g, '').toLowerCase().replace(/m/g, '').replace(/:00(?=[ap])/g, '');
};

const FlightDetailModal = ({ flight, onClose, onEditTrip, onRemoveFlight }) => {
  if (!flight) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[200] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-sky-500 p-6 text-white relative">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Plane size={24} />
            <span className="text-2xl font-black uppercase tracking-tighter">{flight.title}</span>
          </div>
          <div className="inline-block bg-white/20 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest">
            {flight.status || 'Scheduled'}
          </div>
        </div>

        <div className="p-6 flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-8">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">Departure</span>
              <div className="flex flex-col">
                <span className="text-2xl font-black text-zinc-900 leading-none">
                  {formatTime(flight.actualDeparture || flight.departureTime)}
                </span>
                {(flight.actualDeparture || flight.scheduledDeparture) && (
                   <span className="text-[10px] font-bold text-zinc-500 mt-1 uppercase">
                     Sched: {formatTime(flight.scheduledDeparture)}
                   </span>
                )}
                {flight.terminal && (
                   <span className="text-[10px] font-black text-sky-600 mt-1 uppercase">
                     Term {flight.terminal} {flight.gate && `• Gate ${flight.gate}`}
                   </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400 text-right">Arrival</span>
              <div className="flex flex-col items-end">
                <span className="text-2xl font-black text-zinc-900 leading-none text-right">
                  {formatTime(flight.actualArrival || flight.arrivalTime)}
                </span>
                {(flight.actualArrival || flight.scheduledArrival) && (
                   <span className="text-[10px] font-bold text-zinc-500 mt-1 uppercase">
                     Sched: {formatTime(flight.scheduledArrival)}
                   </span>
                )}
                {flight.arrivalTerminal && (
                   <span className="text-[10px] font-black text-sky-600 mt-1 uppercase text-right">
                     Term {flight.arrivalTerminal} {flight.arrivalGate && `• Gate ${flight.arrivalGate}`}
                   </span>
                )}
              </div>
            </div>
          </div>

          <div className="h-px bg-zinc-100" />

          <div className="flex flex-col gap-4">
            <button 
              onClick={() => {
                onEditTrip(flight.trip);
                onClose();
              }}
              className="w-full bg-zinc-900 text-white py-3 rounded-lg font-black uppercase tracking-widest text-xs hover:bg-zinc-800 transition-colors shadow-lg shadow-zinc-200"
            >
              View Full Trip Details
            </button>
            <button 
              onClick={() => {
                if (!window.confirm("Remove this flight from the trip? This can't be undone.")) return;
                onRemoveFlight(flight);
                onClose();
              }}
              className="w-full bg-red-50 text-red-600 py-2 rounded-lg font-black uppercase tracking-widest text-[10px] hover:bg-red-100 transition-colors"
            >
              Remove Flight from Trip
            </button>
            <button 
              onClick={onClose}
              className="w-full bg-white text-zinc-500 py-2 rounded-lg font-bold text-xs hover:bg-zinc-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RECOMMENDATION_BATCH_SIZE = 5;

const capRecommendationsByType = (items, limits) => {
  const totalsByType = {};
  items.forEach(({ item }) => {
    if (item.isRecommendation) {
      totalsByType[item.type] = (totalsByType[item.type] || 0) + 1;
    }
  });

  const seenByType = {};
  const cappedItems = [];
  items.forEach((entry) => {
    const { item } = entry;
    if (!item.isRecommendation) {
      cappedItems.push(entry);
      return;
    }

    const type = item.type;
    const limit = limits[type] || RECOMMENDATION_BATCH_SIZE;
    seenByType[type] = (seenByType[type] || 0) + 1;
    if (seenByType[type] > limit) return;

    cappedItems.push(entry);
    if (seenByType[type] === limit && totalsByType[type] > limit) {
      cappedItems.push({
        isRecommendationReveal: true,
        type,
        remaining: totalsByType[type] - limit,
      });
    }
  });

  return cappedItems;
};

const DroppableDay = ({
  day,
  isMainMonth,
  isToday,
  isPast,
  isTravelDay,
  isOutsideDateFilter,
  dayId,
  monthKey,
  dayItems,
  maxSectionItems,
  baseTzToHide,
  profile,
  hiddenCount,
  handleDayWheel,
  dispatch,
  navigate,
  setSelectedFlight,
  getItemColor,
  toggleTask,
  hoveredEventId,
  setHoveredEventId,
  fontSize,
  cellHeight,
  collapsedHeaderIds = {},
  showTinyWeather,
  showFullWeather,
  showDayCounters,
  showEventLocations,
  isSegmented,
  now,
  firstLocWeather,
  lastLocWeather,
  firstLocCity,
  lastLocCity,
  firstLocTzAbbr,
  isCompact,
  visibleCalendars,
  visibleSections,
  habits,
  habitLogs,
  handleSaveHabit,
  handleLogHabit,
  authUser,
  mapStartDate,
  mapEndDate,
  setMapStartDate,
  setMapEndDate,
  setSelectedTripId,
  dragStartRef,
  isCalendarBold,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `droppable-${monthKey}-${dayId}`,
    data: { type: 'day', dayId },
  });
  const [recommendationLimits, setRecommendationLimits] = useState({});

  const heightMap = {
    small: '110px',
    medium: '220px',
    large: '440px',
    xl: '880px',
  };

  const isFirstOfMonth = day.getDate() === 1;
  const monthAbbr = day.toLocaleDateString(undefined, { month: 'short' });



  const handleDayWheelRef = useRef(handleDayWheel);
  handleDayWheelRef.current = handleDayWheel;

  useEffect(() => {
    const el = document.getElementById(`day-box-${monthKey}-${dayId}`);
    if (!el) return;
    const handler = (e) => {
      if (handleDayWheelRef.current) handleDayWheelRef.current(e);
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [monthKey, dayId]);

  return (
    <div
      id={`day-box-${monthKey}-${dayId}`}
      ref={setNodeRef}
      style={{ height: heightMap[cellHeight] || '110px' }}
      className={`flex flex-col relative ${isMainMonth ? (isPast ? 'bg-zinc-50/50' : 'bg-white') : 'bg-transparent'} transition-colors ${isMainMonth ? 'cursor-pointer group/day' : 'pointer-events-none'} ${isOver && isMainMonth ? 'bg-blue-50 ring-2 ring-blue-400 inset-0 z-10' : isMainMonth ? 'hover:bg-zinc-50/50' : ''} shadow-[inset_-1px_-1px_0_0_#f4f4f5]`}
      onMouseDown={(e) => {
        if (e.shiftKey && isMainMonth) {
          e.preventDefault();
          dragStartRef.current = dayId;
        }
      }}
      onMouseEnter={(e) => {
        if (e.buttons === 1 && e.shiftKey && dragStartRef?.current && isMainMonth) {
          const min = dayId < dragStartRef.current ? dayId : dragStartRef.current;
          const max = dayId > dragStartRef.current ? dayId : dragStartRef.current;
          setMapStartDate(min);
          setMapEndDate(max);
          setSelectedTripId('all');
        }
      }}
      onMouseUp={(e) => {
        if (e.shiftKey && dragStartRef?.current && isMainMonth) {
          if (dayId === dragStartRef.current) {
            if (!mapStartDate || mapStartDate !== mapEndDate) {
              setMapStartDate(dayId);
              setMapEndDate(dayId);
              setSelectedTripId('all');
            } else {
              const min = dayId < mapStartDate ? dayId : mapStartDate;
              const max = dayId > mapStartDate ? dayId : mapStartDate;
              setMapStartDate(min);
              setMapEndDate(max);
              setSelectedTripId('all');
            }
          }
          dragStartRef.current = null;
        }
      }}
      onClick={(e) => {
        if (e.shiftKey) return;
        if (isMainMonth) dispatch({ type: 'OPEN_ADD_EVENT', payload: dayId });
      }}
    >
      
      <div className="flex flex-col flex-1 min-h-0 z-10">
        {isMainMonth && hiddenCount > 0 && (
          <div
            title={`${hiddenCount} more items`}
            className="absolute top-0 right-0 w-0 h-0 border-t-[12px] border-l-[12px] border-l-transparent z-20"
            style={{
              borderTopColor: `rgba(0,0,0,${0.05 + (Math.min(hiddenCount, 50) / 50) * 0.95})`,
            }}
          />
        )}
        {isMainMonth && (
          <div
            style={{ height: `${fontSize * 2.4}px` }}
            className="flex items-center justify-center px-2 relative z-0 border-b border-zinc-100"
          >
            {/* Tiny Corner Weather Widgets */}
            {showTinyWeather && firstLocWeather && (
              <div className="absolute top-0.5 left-2 z-10">
                <DailyWeather weather={firstLocWeather} fontSize={fontSize - 1} />
              </div>
            )}
            
            {showTinyWeather && isTravelDay && lastLocWeather && (
              <div className="absolute top-0.5 right-2 z-10">
                <DailyWeather weather={lastLocWeather} fontSize={fontSize - 1} />
              </div>
            )}

                <div className="relative flex items-center justify-center">
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch({ type: 'OPEN_DAY_DETAIL', payload: dayId });
                    }}
                    style={{
                      fontSize: `${fontSize}px`,
                      width: `${fontSize * 2.2}px`,
                      height: `${fontSize * 2.2}px`,
                      padding: '0',
                    }}
                    className={`font-black flex items-center justify-center rounded-full hover:scale-110 transition-transform z-10 flex-shrink-0 ${isToday ? 'bg-zinc-800 text-white shadow-md' : 'text-zinc-900 group-hover/day:bg-zinc-200/50'}`}
                  >
                    {day.getDate()}
                  </div>
                  {!isToday && isMainMonth && (
                    <div className="absolute left-full ml-1 opacity-0 group-hover/day:opacity-100 transition-opacity whitespace-nowrap text-[9px] font-bold text-zinc-400">
                      {(() => {
                        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                        const target = new Date(day.getFullYear(), day.getMonth(), day.getDate());
                        const diffTime = target - today;
                        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                        return diffDays > 0 ? `+${diffDays}d` : `${diffDays}d`;
                      })()}
                    </div>
                  )}
                </div>
          </div>
        )}
        <div className="flex-1 min-h-0 w-full relative flex flex-col z-10 day-content-area overflow-hidden custom-scrollbar-hidden scroll-smooth">
          {isMainMonth && !showFullWeather && (() => {
            const googleEvents = dayItems.filter(m => m.item.type === 'google-event');
            const standardEvents = dayItems.filter(m => m.item.type === 'event' || m.item.type === 'tentative');
            const flights = dayItems.filter(m => m.item.type === 'flight');
            const hotels = dayItems.filter(m => m.item.type === 'hotel');
            const birthdays = dayItems.filter(m => m.item.type === 'birthday');
            const holidays = dayItems.filter(m => m.item.type === 'holiday');
            const travelEvents = [...flights, ...hotels, ...birthdays, ...holidays];
            const taskEvents = dayItems.filter(m => m.item.type === 'task');
            const recEvents = dayItems.filter(m => ['concert', 'restaurant', 'drinks', 'coffee', 'sight', 'musicvenue', 'wanttogo'].includes(m.item.type));
            const coveredTypes = ['google-event', 'event', 'tentative', 'flight', 'hotel', 'birthday', 'holiday', 'task', 'concert', 'restaurant', 'drinks', 'coffee', 'sight', 'musicvenue', 'wanttogo', 'tz-header', 'trip'];
            const otherEvents = dayItems.filter(m => !coveredTypes.includes(m.item.type));
            
            const sectionsRaw = [
               googleEvents,
               standardEvents,
               { items: travelEvents, isTravel: true },
               taskEvents,
               recEvents,
               otherEvents
            ];
            
            const sortSection = (secItems, isTravelSection) => {
              return [...secItems].sort((aObj, bObj) => {
                const a = aObj.item;
                const b = bObj.item;
                if (isTravelSection) {
                  const s3R = { flight: 1, hotel: 2, birthday: 3, holiday: 4 };
                  if (s3R[a.type] !== s3R[b.type]) return s3R[a.type] - s3R[b.type];
                }
                if (a.isRecommendation && b.isRecommendation) {
                  if (a.minDistance !== undefined && b.minDistance !== undefined) {
                     return a.minDistance - b.minDistance;
                  }
                }
                const isAllDayA = a.isAllDay || (!a.startTime && !a.departureTime && !a.checkInTime);
                const isAllDayB = b.isAllDay || (!b.startTime && !b.departureTime && !b.checkInTime);
                if (isAllDayA && !isAllDayB) return -1;
                if (!isAllDayA && isAllDayB) return 1;
                const timeA = a.startTime || a.departureTime || a.checkInTime || '23:59:59';
                const timeB = b.startTime || b.departureTime || b.checkInTime || '23:59:59';
                const cleanA = timeA.includes('T') ? timeA.split('T')[1] : timeA;
                const cleanB = timeB.includes('T') ? timeB.split('T')[1] : timeB;
                const tzA = a.timezone || a.timezoneSource || 'home';
                const tzB = b.timezone || b.timezoneSource || 'home';
                const absA =  getAbsoluteTimeMs ? getAbsoluteTimeMs(a.startDate, cleanA, tzA) : 0;
                const absB =  getAbsoluteTimeMs ? getAbsoluteTimeMs(b.startDate, cleanB, tzB) : 0;
                if (absA !== absB) return absA - absB;
                return 0;
              });
            };

            let visibleSecIdx = -1;
            return sectionsRaw.map((secData, originalSecIdx) => {
              const groupName = ['group-google', 'group-events', 'group-travel', 'group-tasks', 'group-recommendations', 'group-other'][originalSecIdx];
              if (visibleSections && !visibleSections.includes(groupName)) return null;
              if (!maxSectionItems || maxSectionItems[originalSecIdx] === 0) return null;
              visibleSecIdx++;
              
              const secItems = Array.isArray(secData) ? secData : secData.items;
              const isTravelSection = !Array.isArray(secData) && secData.isTravel;
              const sorted = sortSection(secItems, isTravelSection);
              const capped = capRecommendationsByType(sorted, recommendationLimits);
              
              let currentTz = baseTzToHide;
              let renderedCount = 0;
              
              return (
                <Fragment key={originalSecIdx}>
                  {isSegmented && visibleSecIdx > 0 && <div className="border-t border-zinc-100 my-1 mx-0" />}
                  <div className="flex flex-col gap-px">
                    {capped.map((m, idx) => {
                      if (m.isRecommendationReveal) {
                        return (
                          <button
                            key={`reveal-${m.type}`}
                            type="button"
                            title={`Show up to 5 more ${m.type} recommendations (${m.remaining} remaining)`}
                            aria-label={`Show up to 5 more ${m.type} recommendations`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setRecommendationLimits((current) => ({
                                ...current,
                                [m.type]: (current[m.type] || RECOMMENDATION_BATCH_SIZE) + RECOMMENDATION_BATCH_SIZE,
                              }));
                            }}
                            style={{
                              height: `${fontSize + 9}px`,
                              fontSize: `${fontSize}px`,
                              backgroundColor: getItemColor({ type: m.type }),
                            }}
                            className="mx-[2px] px-1.5 flex items-center justify-center flex-shrink-0 rounded-md text-white font-black tracking-tight hover:brightness-110 pointer-events-auto active:scale-[0.98] transition-all"
                          >
                            +5
                          </button>
                        );
                      }

                      let itemTz = m.item.timezone || m.item.timezoneSource;
                      if (!itemTz) itemTz = 'home';
                      if (itemTz === profile?.cities?.[0]?.timezone) itemTz = 'home';
                      
                      let showHeader = false;
                      let tzName = itemTz;
                      let tzAbbr = '';
                      
                      if (!m.item.isAllDay && m.item.type !== 'task' && !m.item.isRecommendation && m.item.type !== 'birthday' && m.item.type !== 'holiday') {
                         if (itemTz !== currentTz && itemTz !== baseTzToHide) {
                            showHeader = true;
                            currentTz = itemTz;
                            
                            if (itemTz === 'home') {
                              tzName = profile?.cities?.[0]?.name?.split(',')[0].trim() || 'Home';
                            } else if (itemTz.startsWith('trip:')) {
                               tzName = 'Trip'; 
                            } else if (itemTz.startsWith('custom:')) {
                               tzName = itemTz.split(':')[1];
                            } else {
                               const city = profile?.cities?.find((c) => c.timezone === itemTz);
                               if (city) tzName = city.name.split(',')[0].trim();
                            }
                            
                            try {
                               const actualTz = profile?.cities?.find(c => c.timezone === itemTz)?.timezone || itemTz;
                               const d = new Date(`${dayId}T12:00:00`);
                               const parts = new Intl.DateTimeFormat('en-US', { timeZone: actualTz, timeZoneName: 'short' }).formatToParts(d);
                               const tzPart = parts.find(p => p.type === 'timeZoneName');
                               if (tzPart) tzAbbr = ` (${tzPart.value})`;
                            } catch (e) {}
                         }
                      }
                      
                      return (
                        <Fragment key={`${m.item.id}-${idx}`}>
                          {showHeader && (() => {
                             renderedCount++;
                             return (
                               <DraggableEvent
                                 m={{ item: { id: `tz-${itemTz}-${idx}`, type: 'tz-header', title: tzName + tzAbbr, isHeader: true, startDate: dayId, endDate: dayId } }}
                                 dayId={dayId}
                                 isTravelDay={isTravelDay}
                                 navigate={navigate}
                                 dispatch={dispatch}
                                 setSelectedFlight={setSelectedFlight}
                                 getItemColor={getItemColor}
                                 toggleTask={toggleTask}
                                 hoveredEventId={hoveredEventId}
                                 setHoveredEventId={setHoveredEventId}
                                 fontSize={fontSize}
                                 isCollapsed={false}
                                 showEventLocations={showEventLocations}
                                 setSelectedTripId={setSelectedTripId}
                                 isCalendarBold={isCalendarBold}
                                 firstLocCity={firstLocCity}
                                 lastLocCity={lastLocCity}
                               />
                             );
                          })()}
                          {(() => { renderedCount++; return null; })()}
                          <DraggableEvent
                            m={m}
                            dayId={dayId}
                            isTravelDay={isTravelDay}
                            navigate={navigate}
                            dispatch={dispatch}
                            setSelectedFlight={setSelectedFlight}
                            getItemColor={getItemColor}
                            toggleTask={toggleTask}
                            hoveredEventId={hoveredEventId}
                            setHoveredEventId={setHoveredEventId}
                            fontSize={fontSize}
                            isCollapsed={!!(collapsedHeaderIds && collapsedHeaderIds[m.item.id])}
                            showEventLocations={showEventLocations}
                            setSelectedTripId={setSelectedTripId}
                            isCalendarBold={isCalendarBold}
                            firstLocCity={firstLocCity}
                            lastLocCity={lastLocCity}
                            isSegmented={isSegmented}
                          />
                        </Fragment>
                      );
                    })}
                    {isSegmented && maxSectionItems && maxSectionItems[originalSecIdx] > renderedCount && (
                       Array.from({ length: maxSectionItems[originalSecIdx] - renderedCount }).map((_, padIdx) => (
                          <div key={`pad-${padIdx}`} style={{ height: `${fontSize + 9}px` }} className="opacity-0 pointer-events-none" />
                       ))
                    )}
                  </div>
                  </Fragment>
                );
            });
          })()}
          {isMainMonth && showFullWeather && (
            <div className={`p-1 flex ${isTravelDay && lastLocWeather ? 'flex-row gap-1' : 'flex-col gap-2'}`}>
               <div className="flex-1 min-w-0">
                 <FullWeatherDisplay weather={firstLocWeather} city={firstLocCity} fontSize={fontSize} />
               </div>
               {isTravelDay && lastLocWeather && (
                 <div className="flex-1 min-w-0">
                   <FullWeatherDisplay weather={lastLocWeather} city={lastLocCity} fontSize={fontSize} />
                 </div>
               )}
            </div>
          )}
          {isMainMonth && !showFullWeather && habits.length > 0 && visibleCalendars.includes('nytm-habit') && (
            <div className="mt-auto px-1 pb-1 flex-shrink-0">
              <HabitTracker
                dayId={dayId}
                monthKey={monthKey}
                habits={habits}
                habitLogs={habitLogs}
                handleLogHabit={handleLogHabit}
                fontSize={fontSize}
                dispatch={dispatch}
                authUser={authUser}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

const MonthRenderer = memo(({
  month,
  calendarItems,
  profile,
  calendarFontSize,
  calendarCellHeight,
  dispatch,
  navigate,
  setSelectedFlight,
  getItemColor,
  toggleTask,
  hoveredEventId,
  setHoveredEventId,
  collapsedHeaderIds,
  now,
  weatherCache,
  tempCoordsCache,
  isPriority,
  isSegmented,
  isCompact,
  isCalendarBold,
  showTinyWeather,
  showFullWeather,
  showDayCounters,
  showEventLocations,
  isTravelStripHidden,
  advancedFilters,
  visibleCalendars,
  visibleSections,
  habits,
  habitLogs,
  handleSaveHabit,
  handleLogHabit,
  authUser,
  mapStartDate,
  mapEndDate,
  setMapStartDate,
  setMapEndDate,
  setSelectedTripId,
  dragStartRef,
  recsWithCat,
  selectedTags,
  tagsByRoot,
  songkickConcerts,
  friendFilter,
  customCenter,
  searchRadius,
  tagsHierarchy,
  availableTags,
  getParentTags,
  getChildTags,
}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (isPriority) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
        }
      },
      { rootMargin: '1000px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isPriority]);

  const isVisible = isPriority || isIntersecting;
  const dayHeight =
    calendarCellHeight === 'small' ? 110 : calendarCellHeight === 'medium' ? 220 : calendarCellHeight === 'large' ? 440 : 880;
  const estimatedHeight = 20 + month.weeks.length * (dayHeight + 1);
  const monthColor = MONTH_COLORS[month.monthIndex] || 'bg-zinc-50';

  if (!isVisible) {
    return (
      <div
        ref={ref}
        data-month-key={month.monthKey}
        style={{ height: `${estimatedHeight}px` }}
        className="w-full bg-white scroll-mt-14"
      />
    );
  }

  return (
    <div
      ref={ref}
      data-month-key={month.monthKey}
      className="scroll-mt-14"
    >
      <div className="flex flex-col gap-px">
        <div
          className={`${monthColor} border-b border-zinc-200 text-sm font-black text-zinc-900 uppercase tracking-[0.3em] sticky top-0 z-30 flex items-center justify-center h-5 shadow-sm`}
        >
          {month.label}
        </div>
        {month.weeks.map((week, wi) => {
          // Compact View Deduplication: 
          // If in compact mode, only the month that "owns" the Sunday of a week renders it.
          // This creates a perfectly seamless flow without duplicating rows.
          if (isCompact && week[0].getMonth() !== month.monthIndex) return null;

          const weekStartId = `${week[0].getFullYear()}-${String(week[0].getMonth() + 1).padStart(2, '0')}-${String(week[0].getDate()).padStart(2, '0')}`;
          const weekEndId = `${week[6].getFullYear()}-${String(week[6].getMonth() + 1).padStart(2, '0')}-${String(week[6].getDate()).padStart(2, '0')}`;
          
          // Process Birthday Recurrence
          const birthdays = (calendarItems || []).filter(item => item.type === 'birthday');
          
          // Inject "My Birthday" from profile
          if (profile?.birthday && visibleCalendars.includes('nytm-birthday')) {
            birthdays.push({
              id: 'my-birthday',
              type: 'birthday',
              personName: profile?.name || 'My',
              startDate: `2000-${profile.birthday}`,
              endDate: `2000-${profile.birthday}`,
              birthYear: profile.birthYear || null,
              bio: profile.bio || '',
              motivationalMessage: profile.motivationalMessage || '',
            });
          }

          const recurringBirthdays = [];
          
          if (birthdays.length > 0) {
            week.forEach(day => {
              const dayId = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
              const dayMonthDay = dayId.substring(5); // "MM-DD"
              
              birthdays.forEach(b => {
                if (b.startDate.substring(5) === dayMonthDay) {
                  const birthYear = parseInt(b.birthYear);
                  const currentYear = day.getFullYear();
                  
                  // Only show if the person was born on or before this year
                  if (!isNaN(birthYear) && currentYear < birthYear) return;

                  const age = !isNaN(birthYear) ? currentYear - birthYear : null;
                  
                  let title = age !== null ? `${b.personName} is ${age} years old!` : `${b.personName}'s Birthday`;
                  if (b.id === 'my-birthday') {
                    const userName = profile?.name || 'My';
                    title = age !== null ? `${userName}'s Birthday - I'm ${age} years old!` : `${userName}'s Birthday`;
                  }

                  recurringBirthdays.push({
                    ...b,
                    originalId: b.id,
                    id: b.id === 'my-birthday' ? `my-birthday-${currentYear}` : `${b.id}-${currentYear}`,
                    startDate: dayId,
                    endDate: dayId,
                    title,
                    displayTime: null,
                    isAllDay: true
                  });
                }
              });
            });
          }

          // Process Flights from Trips
          const flightItems = [];
          if (visibleCalendars.includes('nytm-flight')) {
            (calendarItems || []).forEach(item => {
              if (item.type === 'trip') {
                const flightsIn = (item.flightIn || []).filter(f => f.trim() !== '');
                const flightsOut = (item.flightOut || []).filter(f => f.trim() !== '');
                const allFlights = [...new Set([...flightsIn, ...flightsOut])];

                allFlights.forEach(ident => {
                  const data = item.flightData?.[ident] || {};
                  const isOut = flightsOut.includes(ident);
                  // Out = Leaving Home for Destination. In = Leaving Destination for Home.
                  const depCity = isOut ? 'home' : item.city;
                  const arrCity = isOut ? item.city : 'home';

                  const depDate = data.departureTime ? data.departureTime.split('T')[0] : (isOut ? item.startDate : item.endDate);
                  const arrDate = data.arrivalTime ? data.arrivalTime.split('T')[0] : (isOut ? item.startDate : item.endDate);

                  const flightDurationStr = data.filed_ete ? `${Math.floor(data.filed_ete / 3600)}h ${Math.floor((data.filed_ete % 3600) / 60)}m` : null;

                  if (data.scheduledDeparture || data.scheduledArrival || data.departureTime || data.arrivalTime) {
                    // Takeoff Pill
                    flightItems.push({
                      id: `flight-${item.id}-${ident}-takeoff`,
                      type: 'flight',
                      isTakeoff: true,
                      title: ident,
                      durationStr: flightDurationStr,
                      startDate: depDate,
                      endDate: depDate,
                      startTime: data.actualDeparture || data.scheduledDeparture || data.departureTime,
                      city: depCity,
                      ...data,
                      trip: item,
                    });
                    // Landing Pill
                    flightItems.push({
                      id: `flight-${item.id}-${ident}-landing`,
                      type: 'flight',
                      isLanding: true,
                      title: ident,
                      startDate: arrDate,
                      endDate: arrDate,
                      startTime: data.actualArrival || data.scheduledArrival || data.arrivalTime,
                      city: arrCity,
                      ...data,
                      trip: item,
                    });
                  } else {
                    // Fallback: If no real-time departure time, use trip start/end date
                    flightItems.push({
                      id: `flight-${item.id}-${ident}`,
                      type: 'flight',
                      title: ident,
                      startDate: depDate,
                      endDate: depDate,
                      ...data,
                      trip: item,
                    });
                  }
                });
              }
            });
          }

          // Process Hotels from Trips
          const hotelItems = [];
          if (visibleCalendars.includes('nytm-hotel')) {
            (calendarItems || []).forEach(item => {
              if (item.type === 'trip' && item.hotels && item.hotels.length > 0) {
                item.hotels.forEach((hotel, hIdx) => {
                  if (hotel.startDate) {
                    hotelItems.push({
                      ...hotel,
                      id: `hotel-in-${item.id}-${hIdx}`,
                      type: 'hotel',
                      title: `Check-in: ${hotel.name}`,
                      startDate: hotel.startDate,
                      endDate: hotel.startDate,
                      startTime: hotel.checkInTime || null,
                      isAllDay: !hotel.checkInTime,
                      city: item.city,
                      trip: item
                    });
                  }
                  if (hotel.endDate) {
                    hotelItems.push({
                      ...hotel,
                      id: `hotel-out-${item.id}-${hIdx}`,
                      type: 'hotel',
                      title: `Check-out: ${hotel.name}`,
                      startDate: hotel.endDate,
                      endDate: hotel.endDate,
                      startTime: hotel.checkOutTime || null,
                      isAllDay: !hotel.checkOutTime,
                      city: item.city,
                      trip: item
                    });
                  }
                });
              }
            });
          }

          // Process Custom Holiday Recurrence
          const customHolidays = (calendarItems || []).filter(item => item.type === 'holiday' && !item.isHoliday);
          const recurringHolidays = [];
          
          if (customHolidays.length > 0) {
            week.forEach(day => {
              const dayId = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
              const dayMonthDay = dayId.substring(5); // "MM-DD"
              
              customHolidays.forEach(h => {
                if (h.startDate.substring(5) === dayMonthDay) {
                  const currentYear = day.getFullYear();
                  recurringHolidays.push({
                    ...h,
                    originalId: h.id,
                    id: `${h.id}-${currentYear}`,
                    startDate: dayId,
                    endDate: dayId,
                    displayTime: null,
                    isAllDay: true,
                    isHoliday: true
                  });
                }
              });
            });
          }

          const allWeekItems = [...(calendarItems || []).filter(item => item.type !== 'birthday' && !(item.type === 'holiday' && !item.isHoliday)), ...recurringBirthdays, ...recurringHolidays, ...flightItems, ...hotelItems];

          const weekItems = allWeekItems.filter(
            (item) => {
              if (item.startDate > weekEndId || item.endDate < weekStartId) return false;
              return true;
            }
          );

          const weekTrips = weekItems.filter((item) => item.type === 'trip');
          
          const getTypeRank = (item) => {
            if (['event', 'flight', 'hotel'].includes(item.type)) return 1;
            if (item.type === 'tentative') return 2;
            if (item.type === 'holiday') return 3;
            if (item.type === 'birthday') return 4;
            if (['concert', 'restaurant', 'drinks', 'coffee', 'sight', 'musicvenue', 'wanttogo'].includes(item.type)) return 5;
            return 99;
          };

          const allDayEvents = weekItems.filter((item) => {
            if (['trip', 'task'].includes(item.type)) return false;
            if (item.isAllDay || item.startDate !== item.endDate) return true;
            return false;
          }).sort((a, b) => {
             const startA = a.startDate || '9999-12-31';
             const startB = b.startDate || '9999-12-31';
             if (startA !== startB) return startA.localeCompare(startB);
             
             const endA = a.endDate || a.startDate || '9999-12-31';
             const endB = b.endDate || b.startDate || '9999-12-31';
             if (endA !== endB) return endB.localeCompare(endA);

             const rankA = getTypeRank(a);
             const rankB = getTypeRank(b);
             if (rankA !== rankB) return rankA - rankB;
             return a.title?.localeCompare(b.title) || 0;
          });

          const timedEvents = weekItems.filter((item) => {
            if (['trip', 'task'].includes(item.type)) return false;
            if (item.isAllDay || item.startDate !== item.endDate) return false;
            return true;
          });

          const taskPriorityWeight = { 'High': 1, 'Medium': 2, 'Low': 3 };
          const weekTasks = weekItems.filter((item) => {
            if (item.type !== 'task') return false;
            if (item.completed) return false;

            const priority = item.priority;
            if (priority === 'High' && !visibleCalendars.includes('nytm-tasks-h')) return false;
            if (priority === 'Medium' && !visibleCalendars.includes('nytm-tasks-m')) return false;
            if (priority === 'Low' && !visibleCalendars.includes('nytm-tasks-l')) return false;

            return true;
          }).sort((a, b) => {
            const pA = taskPriorityWeight[a.priority || 'High'] || 99;
            const pB = taskPriorityWeight[b.priority || 'High'] || 99;
            return pA - pB;
          });

          const weekPlans = []; // Deprecated, merged into timedEvents

          const homeCityName = profile?.cities?.[0]?.name || 'Home';
          const cleanCityName = (name) => {
            if (!name) return '';
            const lower = name.toLowerCase();
            if (lower.includes('district of columbia')) return 'D.C.';
            return name.split(',')[0].trim();
          };

          const getDistance = (lat1, lon1, lat2, lon2) => {
            if (!lat1 || !lon1 || !lat2 || !lon2) return 9999;
            const R = 3958.8; // Radius of the earth in miles
            const dLat = (lat2 - lat1) * (Math.PI / 180);
            const dLon = (lon2 - lon1) * (Math.PI / 180);
            const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
                      Math.sin(dLon / 2) * Math.sin(dLon / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            return R * c;
          };

          // Group timed events by day and then by timezoneSource
          const dailyTzGroups = {};
          week.forEach((day) => {
            const dayId = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
            dailyTzGroups[dayId] = {};

            // Calculate if this is a travel day
            const outTrips = weekTrips.filter((t) => t.endDate === dayId);
            const stayTrips = weekTrips.filter(
              (t) => t.startDate < dayId && t.endDate > dayId
            );
            const inTrips = weekTrips.filter((t) => t.startDate === dayId);
            const homeCity = profile?.cities?.[0];

            const seenLocs = new Set();
            outTrips.forEach((t) => seenLocs.add(`trip:${t.id}`));
            stayTrips.forEach((t) => seenLocs.add(`trip:${t.id}`));
            inTrips.forEach((t) => seenLocs.add(`trip:${t.id}`));

            // Get timed events for this day
            const dayTimed = timedEvents.filter(
              (e) => dayId >= e.startDate && dayId <= e.endDate
            );

            // Inject Recommendations
            const isWithinDateFilter = (!mapStartDate || !mapEndDate) || (dayId >= mapStartDate && dayId <= mapEndDate);
            const showFood = visibleCalendars.includes('nytm-restaurant');
            const showDrinks = visibleCalendars.includes('nytm-drinks');
            const showCoffee = visibleCalendars.includes('nytm-coffee');
            const showSights = visibleCalendars.includes('nytm-sight');
            const showShopping = visibleCalendars.includes('nytm-shopping');
            const showConcerts = visibleCalendars.includes('nytm-concert');
            const showMusicVenues = visibleCalendars.includes('nytm-musicvenue');
            const showWantToGo = visibleCalendars.includes('nytm-wanttogo');
            const showAnyRecs = showFood || showDrinks || showCoffee || showSights || showShopping || showMusicVenues || showWantToGo;
            if (isWithinDateFilter && showAnyRecs && recsWithCat?.length > 0) {
              let locsForDay = [];
              if (customCenter) {
                locsForDay.push({ lat: customCenter.lat, lng: customCenter.lng, tz: 'home', city: customCenter.name || 'Custom' });
              } else {
                locsForDay = getActiveLocationsForDate(dayId, day, weekTrips, weekItems, homeCity);
              }

              const filteredRecs = recsWithCat.filter(rec => {
                 if (friendFilter === 'me' && rec.userId !== authUser?.uid) return false;
                 if (friendFilter !== 'all' && friendFilter !== 'me' && rec.userId !== friendFilter) return false;
                 
                 const isFood = rec._cat.isFood;
                 const isDrinks = rec._cat.isDrinks;
                 const isCoffee = rec._cat.isCoffee;
                 const isSight = rec._cat.isSight;
                 const isShopping = rec._cat.isShopping;
                 const isMusicVenue = rec._cat.isMusicVenue;
                 const isWantToGo = rec._cat.isWantToGo;
                 
                 const anyPrimarySelected = showFood || showDrinks || showCoffee || showSights || showShopping || showMusicVenues;
                 
                 // If "Want to Go" is ON, but no primary categories are ON, it shows NOTHING.
                 if (showWantToGo && !anyPrimarySelected) return false;
                 
                 let matchesPrimary = false;
                 if (showFood && isFood) matchesPrimary = true;
                 if (showDrinks && isDrinks) matchesPrimary = true;
                 if (showCoffee && isCoffee) matchesPrimary = true;
                 if (showSights && isSight) matchesPrimary = true;
                 if (showShopping && isShopping) matchesPrimary = true;
                 if (showMusicVenues && isMusicVenue) matchesPrimary = true;
                 
                 if (anyPrimarySelected) {
                     if (!matchesPrimary) return false; // Must match at least one selected primary category
                     if (showWantToGo && !isWantToGo) return false; // Intersection filter
                 } else {
                     return false; // No primary categories selected and showWantToGo is false
                 }
                 
                 const activeTypes = [];
                 if (showFood && isFood) activeTypes.push('restaurant');
                 if (showDrinks && isDrinks) activeTypes.push('drinks');
                 if (showCoffee && isCoffee) activeTypes.push('coffee');
                 if (showSights && isSight) activeTypes.push('sight');
                 if (showShopping && isShopping) activeTypes.push('shopping');
                 if (showMusicVenues && isMusicVenue) activeTypes.push('musicvenue');

                 let passesTagFilter = false;
                 if (advancedFilters === null) {
                     passesTagFilter = true;
                 } else {
                     for (const type of activeTypes) {
                         const tagsForType = tagsByRoot[type];
                         if (!tagsForType || tagsForType.length === 0) {
                             // passesTagFilter remains false (0 tags = 0 items)
                         } else {
                             if (tagsForType.some(t => rec.tags?.includes(t))) {
                                 passesTagFilter = true;
                                 break;
                             }
                         }
                     }
                 }
                 if (!passesTagFilter) return false;

                  if (locsForDay.length > 0) {
                    let matchFound = false;
                    for (const loc of locsForDay) {
                      const R = 3958.8; // miles
                      const dLat = (rec.lat - loc.lat) * Math.PI / 180;
                      const dLon = (rec.lng - loc.lng) * Math.PI / 180;
                      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                                Math.cos(loc.lat * Math.PI / 180) * Math.cos(rec.lat * Math.PI / 180) *
                                Math.sin(dLon/2) * Math.sin(dLon/2);
                      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                      const distance = R * c;

                      if (distance <= searchRadius) {
                         matchFound = true;
                         break;
                      }
                    }
                    if (!matchFound) return false;
                  }

                 if (rec.weekdayDescriptions && rec.weekdayDescriptions.length === 7) {
                   const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                   const dayName = dayNames[day.getDay()];
                   const desc = rec.weekdayDescriptions.find(d => d.startsWith(dayName));
                   if (desc && desc.toLowerCase().includes('closed')) {
                     const nowMs = Date.now();
                     const updatedAt = rec.updatedAt?.seconds * 1000 || rec.createdAt?.seconds * 1000 || 0;
                     if (nowMs - updatedAt <= 7 * 24 * 60 * 60 * 1000) {
                       return false;
                     }
                   }
                 }

                 return true;
              });

              const groupedRecs = {};
              filteredRecs.forEach(rec => {
                 if (!groupedRecs[rec.placeId]) {
                    groupedRecs[rec.placeId] = { ...rec, users: new Set([rec.userId]) };
                 } else {
                    groupedRecs[rec.placeId].users.add(rec.userId);
                 }
              });

              Object.values(groupedRecs).forEach(rec => {
                const isWantToGo = rec._cat.isWantToGo;
                let matchLoc = null;
                const radius = searchRadius === '' ? 0 : (searchRadius ?? 50);
                let minDistance = Infinity;

                for (const loc of locsForDay) {
                  const dLat = (rec.lat - loc.lat) * Math.PI / 180;
                  const dLon = (rec.lng - loc.lng) * Math.PI / 180;
                  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                            Math.cos(loc.lat * Math.PI / 180) * Math.cos(rec.lat * Math.PI / 180) *
                            Math.sin(dLon/2) * Math.sin(dLon/2);
                  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                  const d = 3958.8 * c;
                  
                  if (d <= radius && d < minDistance) {
                    matchLoc = loc;
                    minDistance = d;
                  }
                }

                if (matchLoc) {
                    let titleStr = rec.placeName;
                    if (rec.weekdayDescriptions && rec.weekdayDescriptions.length === 7) {
                       const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                       const dayName = dayNames[day.getDay()];
                       const desc = rec.weekdayDescriptions.find(d => d.startsWith(dayName));
                       if (desc) {
                           const timePart = desc.substring(desc.indexOf(':') + 1).trim();
                           if (timePart && !timePart.toLowerCase().includes('closed')) {
                               const timeFormatted = timePart.replace(/:00/g, '').replace(/\s/g, '').replace(/AM/gi, 'a').replace(/PM/gi, 'p');
                               titleStr = `${rec.placeName} ${timeFormatted}`;
                           }
                       }
                    }
                    if (minDistance !== Infinity) {
                        titleStr = `${titleStr} (${minDistance.toFixed(1)} mi)`;
                    }

                    if (isWantToGo && !showWantToGo) return; // Hide completely if Want to Go toggle is off

                    let recType = 'restaurant';
                    const isFood = rec._cat.isFood;
                    const isDrinks = rec._cat.isDrinks;
                    const isCoffee = rec._cat.isCoffee;
                    const isSight = rec._cat.isSight;
                    const isShopping = rec._cat.isShopping;
                    const isMusicVenue = rec._cat.isMusicVenue;
                    
                    const activeTypes = [];
                    const anyPrimaryFilterActive = showFood || showDrinks || showCoffee || showSights || showShopping || showMusicVenues;

                    if (anyPrimaryFilterActive) {
                        if (showFood && isFood) activeTypes.push('restaurant');
                        if (showDrinks && isDrinks) activeTypes.push('drinks');
                        if (showCoffee && isCoffee) activeTypes.push('coffee');
                        if (showSights && isSight) activeTypes.push('sight');
                        if (showShopping && isShopping) activeTypes.push('shopping');
                        if (showMusicVenues && isMusicVenue) activeTypes.push('musicvenue');
                    } else if (showWantToGo && isWantToGo) {
                        // Only Want to Go is active, show it under its true category
                        if (isFood) activeTypes.push('restaurant');
                        else if (isDrinks) activeTypes.push('drinks');
                        else if (isCoffee) activeTypes.push('coffee');
                        else if (isSight) activeTypes.push('sight');
                        else if (isShopping) activeTypes.push('shopping');
                        else if (isMusicVenue) activeTypes.push('musicvenue');
                        else activeTypes.push('restaurant'); // Fallback
                    }

                    if (activeTypes.length === 0) return; // Skip if no active filters match
                    
                    recType = activeTypes[0]; // Primary type if any

                   dayTimed.push({
                     id: `rec-${rec.placeId}-${dayId}`,
                     type: recType,
                     multiTypes: activeTypes,
                     title: titleStr,
                     startDate: dayId,
                     endDate: dayId,
                     city: matchLoc.city,
                     timezoneSource: matchLoc.tz,
                     isRecommendation: true,
                     isReadOnly: true,
                     isWantToGo: isWantToGo,
                     note: rec.note,
                     data: rec,
                     minDistance: minDistance
                   });
                }
              });
            }

            if (showConcerts && songkickConcerts?.length > 0) {
              songkickConcerts.forEach(concert => {
                if (concert.isCanceled || !concert.lat || !concert.lng) return;
                if (concert.startDate !== dayId) return;

                let locsForDay = [];
                if (customCenter) {
                  locsForDay.push({ lat: customCenter.lat, lng: customCenter.lng, tz: 'home', city: customCenter.name || 'Custom' });
                } else {
                  locsForDay = getActiveLocationsForDate(dayId, day, weekTrips, weekItems, homeCity);
                }

                let matchLoc = null;
                const radius = searchRadius === '' ? 0 : (searchRadius ?? 50);
                let minDistance = Infinity;

                for (const loc of locsForDay) {
                  const d = getDistance(loc.lat, loc.lng, concert.lat, concert.lng);
                  if (d <= radius && d < minDistance) {
                    matchLoc = loc;
                    minDistance = d;
                  }
                }

                if (matchLoc) {
                  dayTimed.push({
                    id: concert.id,
                    type: 'concert',
                    multiTypes: ['concert'],
                    title: `${concert.title || concert.artist} (${minDistance.toFixed(1)} mi)`,
                    startDate: dayId,
                    endDate: dayId,
                    city: matchLoc.city,
                    timezoneSource: matchLoc.tz,
                    isRecommendation: true,
                    isReadOnly: true,
                    data: { ...concert, _distance: minDistance.toFixed(1) },
                    minDistance: minDistance
                  });
                }
              });
            }

            // Also check for custom locations in day events to trigger headers
            let hasTentativeWithCity = false;
            dayTimed.forEach(e => {
              if (e.city) {
                if (e.type === 'tentative') hasTentativeWithCity = true;
              }
            });

            const isUserTraveling = outTrips.length > 0 || inTrips.length > 0 || hasTentativeWithCity;
            let baseTzToHide = null;
            if (!isUserTraveling) {
               baseTzToHide = stayTrips.length > 0 ? `trip:${stayTrips[0].id}` : 'home';
            }

            dayTimed.forEach((item) => {
              let tz = item.timezoneSource || 'home';
              if (tz === profile?.cities?.[0]?.timezone) tz = 'home';
              
              // Force planning events with a city to group by their city name
              if (item.type === 'tentative' && item.city) {
                 const matchingTrip = [...outTrips, ...stayTrips, ...inTrips].find(t => t.city?.split(',')[0].trim() === item.city?.split(',')[0].trim());
                 if (matchingTrip) tz = `trip:${matchingTrip.id}`;
                 else tz = `custom:${item.city}`;
              }
              // If it's a custom location not tied to a trip/home, group it by its city
              else if (item.city && (!tz || tz === 'home' || tz === 'trip')) {
                 const matchingTrip = [...outTrips, ...stayTrips, ...inTrips].find(t => t.city?.split(',')[0].trim() === item.city?.split(',')[0].trim());
                 if (matchingTrip) tz = `trip:${matchingTrip.id}`;
                 else tz = `custom:${item.city}`;
              }
              if (!dailyTzGroups[dayId][tz]) dailyTzGroups[dayId][tz] = [];
              dailyTzGroups[dayId][tz].push(item);
            });

            // Store the sorting order for this specific day
            const order = [];
            outTrips.forEach((t) => order.push(`trip:${t.id}`));
            stayTrips.forEach((t) => order.push(`trip:${t.id}`));
            order.push('home');
            inTrips.forEach((t) => order.push(`trip:${t.id}`));
            dailyTzGroups[dayId]._order = order;
            dailyTzGroups[dayId]._baseTzToHide = baseTzToHide;
          });


          const sortChronological = (arr) => {
            return arr.sort((a, b) => {
               const timeA = a.startTime || a.departureTime || a.checkInTime || '23:59:59';
               const timeB = b.startTime || b.departureTime || b.checkInTime || '23:59:59';
               const cleanTimeA = timeA.includes('T') ? timeA.split('T')[1] : timeA;
               const cleanTimeB = timeB.includes('T') ? timeB.split('T')[1] : timeB;
               const timeDiff = cleanTimeA.localeCompare(cleanTimeB);
               if (timeDiff !== 0) return timeDiff;

               const rankA = getTypeRank(a);
               const rankB = getTypeRank(b);
               if (rankA !== rankB) return rankA - rankB;

               if (rankA === 5 && a.minDistance !== undefined && b.minDistance !== undefined) {
                 return a.minDistance - b.minDistance;
               }
               return 0;
            });
          };

          const processedTimed = [];
          Object.entries(dailyTzGroups).forEach(([dayId, groups]) => {
              const order = groups._order || [];
              const baseTzToHide = groups._baseTzToHide;
              const sortedTz = Object.keys(groups).filter(k => k !== '_order' && k !== '_baseTzToHide').sort((a, b) => {
                const idxA = order.indexOf(a);
                const idxB = order.indexOf(b);
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return a.localeCompare(b);
              });

              sortedTz.forEach((tz) => {
                let tzName = tz;
                let actualTz = null;
                if (tz === 'home') {
                  tzName = homeCityName;
                  actualTz = profile?.cities?.[0]?.timezone;
                } else if (tz.startsWith('trip:')) {
                  const tripId = tz.split(':')[1];
                  const trip = weekTrips.find((t) => t.id === tripId);
                  tzName = trip ? trip.city || 'Trip' : 'Trip';
                  actualTz = trip?.timezone;
                } else if (tz.startsWith('custom:')) {
                  tzName = tz.split(':')[1];
                  const city = profile?.cities?.find((c) => c.name.split(',')[0].trim().toUpperCase() === tzName.split(',')[0].trim().toUpperCase());
                  if (city) actualTz = city.timezone;
                } else {
                  const city = profile?.cities?.find((c) => c.timezone === tz);
                  if (city) {
                    tzName = city.name;
                    actualTz = city.timezone;
                  }
                }

                let tzAbbr = '';
                if (actualTz) {
                   try {
                     const d = new Date(`${dayId}T12:00:00`);
                     const parts = new Intl.DateTimeFormat('en-US', { timeZone: actualTz, timeZoneName: 'short' }).formatToParts(d);
                     const tzPart = parts.find(p => p.type === 'timeZoneName');
                     if (tzPart) tzAbbr = ` (${tzPart.value})`;
                   } catch (e) { console.error(e); }
                }

                if (tz !== baseTzToHide) {
                   const headerId = `tz-header-${tz}-${dayId}`;
                   processedTimed.push({
                     id: headerId,
                     type: 'tz-header',
                     title: cleanCityName(tzName) + tzAbbr,
                     startDate: dayId,
                     endDate: dayId,
                   });
                }
                processedTimed.push(...sortChronological(groups[tz]));
              });
          });

          const finalItems = [...allDayEvents, ...processedTimed, ...weekTasks, ...weekPlans];

          return (
            <div
              key={`${wi}-${weekStartId}`}
              data-week-start={weekStartId}
              data-week-end={weekEndId}
              className="flex flex-col gap-px w-full relative bg-zinc-200 week-container"
            >
              {/* Background Layers for Solid Spacer Strips & Month Labels */}
              {!isCompact && (() => {
                const strips = [];
                let currentStart = -1;
                let currentMonth = -1;

                for (let i = 0; i < 7; i++) {
                  const day = week[i];
                  const isMainMonth = day.getMonth() === month.monthIndex;

                  if (!isMainMonth) {
                    if (currentStart === -1) {
                      currentStart = i;
                      currentMonth = day.getMonth();
                    } else if (day.getMonth() !== currentMonth) {
                      strips.push({
                        start: currentStart,
                        end: i - 1,
                        day: week[currentStart],
                      });
                      currentStart = i;
                      currentMonth = day.getMonth();
                    }
                  } else {
                    if (currentStart !== -1) {
                      strips.push({
                        start: currentStart,
                        end: i - 1,
                        day: week[currentStart],
                      });
                      currentStart = -1;
                    }
                  }
                }
                if (currentStart !== -1) {
                  strips.push({ start: currentStart, end: 6, day: week[currentStart] });
                }

                return strips.map((s, idx) => {
                  const firstOfMonth = new Date(month.year, month.monthIndex, 1);
                  const isLeading = s.day < firstOfMonth;

                  let targetMonth = month.monthIndex;
                  let targetYear = month.year;

                  if (!isLeading) {
                    targetMonth = (month.monthIndex + 1) % 12;
                    if (targetMonth === 0) targetYear++;
                  }

                  return (
                    <div
                      key={`spacer-strip-${idx}`}
                      className="absolute top-0 bottom-0 bg-zinc-800 z-20 flex flex-col items-center justify-center pointer-events-none opacity-100 select-none"
                      style={{
                        left: `calc((100% / 7) * ${s.start})`,
                        width: `calc((100% / 7) * ${s.end - s.start + 1})`,
                      }}
                    >
                      <span className="text-4xl font-black text-white leading-none">
                        {new Date(targetYear, targetMonth)
                          .toLocaleDateString(undefined, { month: 'short' })
                          .toUpperCase()}
                      </span>
                      <span className="text-lg font-black text-white leading-none">
                        {targetMonth + 1}/{targetYear.toString().slice(-2)}
                      </span>
                    </div>
                  );
                });
              })()}
              {/* Trip Strip */}
              {!isTravelStripHidden && (
                <div
                  style={{ height: `${calendarFontSize * 1.4}px` }}
                  className="relative grid grid-cols-7 gap-px w-full bg-transparent overflow-hidden shrink-0 z-10"
                >
                  {week.map((day) => {
                    const dayId = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                    const isMainMonth = isCompact || day.getMonth() === month.monthIndex;
                    return (
                      <div
                        key={dayId}
                        className={`flex items-center justify-center transition-all ${isMainMonth ? 'bg-zinc-50 cursor-pointer hover:brightness-95' : 'bg-transparent'}`}
                        onClick={() => {
                          if (isMainMonth) {
                            dispatch({
                              type: 'OPEN_ADD_EVENT',
                              payload: { date: dayId, type: 'trip' },
                            });
                          }
                        }}
                      />
                    );
                  })}

                  {/* Home City & Trip Overlays */}
                  <div className="absolute inset-0 pointer-events-none z-10">
                    {/* Home City Labels */}
                    {(() => {
                      const homeLabels = [];
                      let currentStart = -1;

                      for (let i = 0; i < 7; i++) {
                        const day = week[i];
                        const dayId = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                        const isMainMonth = isCompact || day.getMonth() === month.monthIndex;
                        
                        const dayTrips = weekTrips.filter(t => dayId >= t.startDate && dayId <= t.endDate);
                        const isFullTrip = dayTrips.some(t => dayId > t.startDate && dayId < t.endDate) || 
                                           (dayTrips.some(t => t.startDate === dayId) && dayTrips.some(t => t.endDate === dayId));
                        const hasAnyTrip = dayTrips.length > 0;

                        if (isMainMonth && !hasAnyTrip) {
                          if (currentStart === -1) currentStart = i;
                        } else {
                          if (currentStart !== -1) {
                            homeLabels.push({ start: currentStart, end: i - 1, type: 'full' });
                            currentStart = -1;
                          }
                          if (isMainMonth && !isFullTrip && hasAnyTrip) {
                            const startsToday = dayTrips.some(t => t.startDate === dayId);
                            const endsToday = dayTrips.some(t => t.endDate === dayId);
                            if (startsToday) homeLabels.push({ start: i, end: i, type: 'first-half' });
                            else if (endsToday) homeLabels.push({ start: i, end: i, type: 'second-half' });
                          }
                        }
                      }
                      if (currentStart !== -1) {
                        homeLabels.push({ start: currentStart, end: 6, type: 'full' });
                      }

                      const homeTz = profile?.cities?.[0]?.timezone;
                      const homeTime = formatTimeForTz(now, homeTz, homeTz);

                      const longestSegIdx = homeLabels.length > 0 ? homeLabels.reduce((maxIdx, seg, idx, arr) => {
                        const currentLen = seg.end - seg.start;
                        const maxLen = arr[maxIdx].end - arr[maxIdx].start;
                        return currentLen > maxLen ? idx : maxIdx;
                      }, 0) : -1;

                      return homeLabels.map((seg, idx) => {
                        const leftOffset = seg.type === 'second-half' ? 'calc(100% / 14)' : '0px';
                        const rightOffset = seg.type === 'first-half' ? 'calc(100% / 14)' : '0px';
                        const isHomeEnd = seg.type === 'first-half';
                        
                        return (
                          <div
                            key={`home-seg-${idx}`}
                            className="absolute top-0 bottom-0 flex items-center px-4"
                            style={{
                              left: `calc((100% / 7) * ${seg.start} + ${leftOffset})`,
                              width: `calc((100% / 7) * ${seg.end - seg.start + 1} - ${leftOffset} - ${rightOffset})`,
                            }}
                          >
                            <div
                              style={{ fontSize: `${calendarFontSize * 0.8}px` }}
                              className={`flex items-center gap-2 font-black text-zinc-400 tracking-tighter uppercase whitespace-nowrap ${isHomeEnd ? 'ml-auto' : ''}`}
                            >
                              {idx === longestSegIdx && (isHomeEnd ? (
                                <>
                                  <span className="opacity-70 tabular-nums lowercase">{homeTime}</span>
                                  {homeTime && <span className="opacity-60">-</span>}
                                  <span className="opacity-80">{cleanCityName(homeCityName)}</span>
                                </>
                              ) : (
                                <>
                                  <span className="opacity-80">{cleanCityName(homeCityName)}</span>
                                  {homeTime && <span className="opacity-60">-</span>}
                                  <span className="opacity-70 tabular-nums lowercase">{homeTime}</span>
                                </>
                              ))}
                            </div>
                          </div>
                        );
                      });
                    })()}

                  {weekTrips.map((trip) => {
                    const itemStart = new Date(trip.startDate + 'T12:00:00');
                    const itemEnd = new Date(trip.endDate + 'T12:00:00');

                    const firstMainIdx = isCompact ? 0 : week.findIndex(
                      (d) => d.getMonth() === month.monthIndex
                    );
                    const lastMainIdx = isCompact ? 6 : week.findLastIndex(
                      (d) => d.getMonth() === month.monthIndex
                    );

                    let effectiveStartDate = trip.startDate;
                    
                    let effectiveEndDate = trip.endDate;

                    if (effectiveStartDate > effectiveEndDate) return null;

                    let tripStartIdx = 0;
                    if (effectiveStartDate > weekStartId) {
                      const foundIdx = week.findIndex(d => 
                        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` === effectiveStartDate
                      );
                      if (foundIdx !== -1) tripStartIdx = foundIdx;
                    }
                    let tripEndIdx = 6;
                    if (effectiveEndDate < weekEndId) {
                      const foundIdx = week.findIndex(d => 
                        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` === effectiveEndDate
                      );
                      if (foundIdx !== -1) tripEndIdx = foundIdx;
                    }

                    

                    const startCol = Math.max(tripStartIdx, firstMainIdx);
                    const endCol = Math.min(tripEndIdx, lastMainIdx);

                    if (startCol > endCol) return null;

                    const isTripStart =
                      trip.startDate >= weekStartId && trip.startDate <= weekEndId;
                    const isTripEnd =
                      trip.endDate >= weekStartId && trip.endDate <= weekEndId;

                    
const isOneDay = trip.startDate === trip.endDate;

                    const leftOffsetPct =
                      isOneDay && isTripStart
                        ? (100 / 28)
                        : isTripStart && startCol === tripStartIdx
                          ? (100 / 14)
                          : 0;
                    const rightOffsetPct =
                      isOneDay && isTripEnd
                        ? (100 / 28)
                        : isTripEnd && endCol === tripEndIdx
                          ? (100 / 14)
                          : 0;

                    const leftPct = (100 / 7) * startCol + leftOffsetPct;
                    const widthPct = (100 / 7) * (endCol - startCol + 1) - leftOffsetPct - rightOffsetPct;

                    const tripCityOnly = cleanCityName(trip.city);
                    const homeTz = profile?.cities?.[0]?.timezone;
                    const tripTime = trip.timezone ? formatTimeForTz(now, trip.timezone, homeTz) : '';

                    const tripLabel = (
                      <>
                        <span className="opacity-80">{tripCityOnly}</span>
                        {trip.title && trip.title !== `Travel to ${trip.city}` && (
                          <span className="mx-1 opacity-60">-</span>
                        )}
                        {trip.title && trip.title !== `Travel to ${trip.city}` && (
                          <span>{trip.title}</span>
                        )}
                      </>
                    );

                    return (
                      <div
                        key={trip.id}
                        className="absolute top-0 bottom-0 flex items-center px-4 pointer-events-auto cursor-pointer border-y border-red-700/30 hover:brightness-110 active:scale-[0.99] transition-all shadow-sm overflow-hidden"
                        style={{
                          left: `${leftPct}%`,
                          backgroundColor: getItemColor(trip),
                          borderTopLeftRadius:
                            isTripStart && startCol === tripStartIdx
                              ? '9999px'
                              : '0',
                          borderBottomLeftRadius:
                            isTripStart && startCol === tripStartIdx
                              ? '9999px'
                              : '0',
                          borderTopRightRadius:
                            isTripEnd && endCol === tripEndIdx
                              ? '9999px'
                              : '0',
                          borderBottomRightRadius:
                            isTripEnd && endCol === tripEndIdx
                              ? '9999px'
                              : '0',
                          width: `calc(${widthPct}% + ${(!isTripEnd || endCol !== tripEndIdx) ? '1px' : '0px'})`,
                          zIndex: 1,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (e.shiftKey) {
                            e.preventDefault();
                            setSelectedTripId(trip.id);
                            return;
                          }
                          dispatch({ type: 'OPEN_EDIT_EVENT', payload: trip });
                        }}
                      >
                        {/* Start Segment Text */}
                        {( (isTripStart && startCol === tripStartIdx) || (!isTripStart && startCol === firstMainIdx) ) && (
                          <div
                            style={{ fontSize: `${calendarFontSize * 0.8}px` }}
                            className="flex items-center gap-2 font-black text-white tracking-tighter whitespace-nowrap"
                          >
                            {tripLabel}
                            {tripTime && <span className="opacity-60">-</span>}
                            <span className="opacity-70 tabular-nums">{tripTime}</span>
                          </div>
                        )}

                        {/* End Segment Text (Only if not same day as start) */}
                        {isTripEnd && endCol === tripEndIdx && !isOneDay && (
                          <div
                            style={{ fontSize: `${calendarFontSize * 0.8}px` }}
                            className="flex items-center gap-2 font-black text-white tracking-tighter whitespace-nowrap ml-auto"
                          >
                            <span className="opacity-70 tabular-nums">{tripTime}</span>
                            {tripTime && !((isTripStart && startCol === tripStartIdx) || (!isTripStart && startCol === firstMainIdx)) && <span className="opacity-60">-</span>}
                            {!((isTripStart && startCol === tripStartIdx) || (!isTripStart && startCol === firstMainIdx)) && tripLabel}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Days Grid */}
            {(() => {
              const maxSectionItems = [0, 0, 0, 0, 0, 0];
              const weekData = week.map((day) => {
                const isMainMonth = isCompact || day.getMonth() === month.monthIndex;
                if (!isMainMonth) return { isMainMonth };
                const dayId = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                
                const dayTrips = (calendarItems || []).filter((e) => e.type === 'trip');
                const stayTrips = dayTrips.filter((t) => t.startDate < dayId && t.endDate > dayId);
                const outTrips = dayTrips.filter((t) => t.endDate === dayId);
                const inTrips = dayTrips.filter((t) => t.startDate === dayId);
                const hasTentativeWithCity = (calendarItems || []).some((e) => e.type === 'tentative' && e.city && dayId >= e.startDate && dayId <= e.endDate);
                const isUserTravelingLocally = outTrips.length > 0 || inTrips.length > 0 || hasTentativeWithCity;
                let baseTzToHide = null;
                if (!isUserTravelingLocally) {
                   baseTzToHide = stayTrips.length > 0 ? `trip:${stayTrips[0].id}` : 'home';
                }
                
                const dItems = finalItems.filter((item) => dayId >= item.startDate && dayId <= item.endDate).map(item => ({ item }));
                
                const googleEvents = dItems.filter(m => m.item.type === 'google-event');
                const standardEvents = dItems.filter(m => m.item.type === 'event' || m.item.type === 'tentative');
                const flights = dItems.filter(m => m.item.type === 'flight');
                const hotels = dItems.filter(m => m.item.type === 'hotel');
                const birthdays = dItems.filter(m => m.item.type === 'birthday');
                const holidays = dItems.filter(m => m.item.type === 'holiday');
                const travelEvents = [...flights, ...hotels, ...birthdays, ...holidays];
                const taskEvents = dItems.filter(m => m.item.type === 'task');
                const recEvents = dItems.filter(m => ['concert', 'restaurant', 'drinks', 'coffee', 'sight', 'musicvenue', 'wanttogo'].includes(m.item.type));
                const coveredTypes = ['google-event', 'event', 'tentative', 'flight', 'hotel', 'birthday', 'holiday', 'task', 'concert', 'restaurant', 'drinks', 'coffee', 'sight', 'musicvenue', 'wanttogo', 'tz-header', 'trip'];
                const otherEvents = dItems.filter(m => !coveredTypes.includes(m.item.type));
                
                const sectionsRaw = [
                  googleEvents,
                  standardEvents,
                  { items: travelEvents, isTravel: true },
                  taskEvents,
                  recEvents,
                  otherEvents
                ];
                
                return { isMainMonth, sectionsRaw, baseTzToHide, dayId };
              });
              
              weekData.forEach(data => {
                if (!data.isMainMonth) return;
                
                const sortSection = (secItems, isTravelSection) => {
                  return [...secItems].sort((aObj, bObj) => {
                    const a = aObj.item;
                    const b = bObj.item;
                    if (isTravelSection) {
                      const s3R = { flight: 1, hotel: 2, birthday: 3, holiday: 4 };
                      if (s3R[a.type] !== s3R[b.type]) return s3R[a.type] - s3R[b.type];
                    }
                    if (a.isRecommendation && b.isRecommendation) {
                      if (a.minDistance !== undefined && b.minDistance !== undefined) {
                         return a.minDistance - b.minDistance;
                      }
                    }
                    const isAllDayA = a.isAllDay || (!a.startTime && !a.departureTime && !a.checkInTime);
                    const isAllDayB = b.isAllDay || (!b.startTime && !b.departureTime && !b.checkInTime);
                    if (isAllDayA && !isAllDayB) return -1;
                    if (!isAllDayA && isAllDayB) return 1;
                    const timeA = a.startTime || a.departureTime || a.checkInTime || '23:59:59';
                    const timeB = b.startTime || b.departureTime || b.checkInTime || '23:59:59';
                    const cleanA = timeA.includes('T') ? timeA.split('T')[1] : timeA;
                    const cleanB = timeB.includes('T') ? timeB.split('T')[1] : timeB;
                    
                    const tzA = a.timezone || a.timezoneSource || 'home';
                    const tzB = b.timezone || b.timezoneSource || 'home';
                    const absA =  getAbsoluteTimeMs ? getAbsoluteTimeMs(a.startDate, cleanA, tzA) : 0;
                    const absB =  getAbsoluteTimeMs ? getAbsoluteTimeMs(b.startDate, cleanB, tzB) : 0;
                    if (absA !== absB) return absA - absB;
                    return 0;
                  });
                };
                
                data.sectionsRaw.forEach((secData, secIdx) => {
                  const secItems = Array.isArray(secData) ? secData : secData.items;
                  if (secItems.length === 0) return;
                  
                  const isTravelSection = !Array.isArray(secData) && secData.isTravel;
                  const sorted = sortSection(secItems, isTravelSection);
                  
                  let currentTz = data.baseTzToHide;
                  let itemCount = 0;
                  sorted.forEach(m => {
                    let itemTz = m.item.timezone || m.item.timezoneSource;
                    if (!itemTz) itemTz = 'home';
                    if (itemTz === profile?.cities?.[0]?.timezone) itemTz = 'home';
                    if (!m.item.isAllDay && m.item.type !== 'task' && !m.item.isRecommendation && m.item.type !== 'birthday' && m.item.type !== 'holiday') {
                       if (itemTz !== currentTz && itemTz !== data.baseTzToHide) {
                          itemCount++;
                          currentTz = itemTz;
                       }
                    }
                    itemCount++;
                  });
                  if (itemCount > maxSectionItems[secIdx]) maxSectionItems[secIdx] = itemCount;
                });
              });

              return (
                <div className="grid grid-cols-7 w-full bg-transparent shrink-0 relative z-10">
                  {week.map((day, di) => {
                    const data = weekData[di];
                    const isMainMonth = isCompact || day.getMonth() === month.monthIndex;
                  const isToday =
                    new Date().toDateString() === day.toDateString() && (isCompact || isMainMonth);

                  const todayStart = new Date();
                  todayStart.setHours(0, 0, 0, 0);
                  const d = new Date(day);
                  d.setHours(0, 0, 0, 0);
                  const isPast = d < todayStart && isMainMonth;
                  const dayId = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;

                  const dayTrips = (calendarItems || []).filter((e) => e.type === 'trip');
                  const outTrips = dayTrips.filter((t) => t.endDate === dayId);
                  const stayTrips = dayTrips.filter(
                    (t) => t.startDate < dayId && t.endDate > dayId
                  );
                  const inTrips = dayTrips.filter((t) => t.startDate === dayId);
                  const homeCity = profile?.cities?.[0];

                  const locs = [];
                  outTrips.forEach((t) => locs.push({ id: t.id, tz: t.timezone, lat: t.lat || tempCoordsCache?.[t.city]?.lat, lng: t.lng || tempCoordsCache?.[t.city]?.lng, city: t.city }));
                  stayTrips.forEach((t) => locs.push({ id: t.id, tz: t.timezone, lat: t.lat || tempCoordsCache?.[t.city]?.lat, lng: t.lng || tempCoordsCache?.[t.city]?.lng, city: t.city }));
                  if (homeCity && stayTrips.length === 0)
                    locs.push({ id: 'home', tz: homeCity.timezone, lat: homeCity.lat || tempCoordsCache?.[homeCity.name]?.lat, lng: homeCity.lng || tempCoordsCache?.[homeCity.name]?.lng, city: homeCity.name });
                  inTrips.forEach((t) => locs.push({ id: t.id, tz: t.timezone, lat: t.lat || tempCoordsCache?.[t.city]?.lat, lng: t.lng || tempCoordsCache?.[t.city]?.lng, city: t.city }));

                  const firstLoc = locs[0];
                  const lastLoc = locs.length > 1 ? locs[locs.length - 1] : null;

                  const firstLocTzAbbr = firstLoc
                    ? formatTzAbbr(now, firstLoc.tz, homeCity?.timezone)
                    : '';

                  const normalize = (val) => (val !== null && val !== undefined) ? Number(val).toFixed(2) : null;
                  const firstLocWeatherKey = firstLoc && normalize(firstLoc.lat) && normalize(firstLoc.lng)
                    ? `${normalize(firstLoc.lat)},${normalize(firstLoc.lng)}`
                    : null;
                  const firstLocWeather = firstLocWeatherKey
                    ? weatherCache[firstLocWeatherKey]?.[dayId]
                    : null;

                  const lastLocWeatherKey = lastLoc && normalize(lastLoc.lat) && normalize(lastLoc.lng)
                    ? `${normalize(lastLoc.lat)},${normalize(lastLoc.lng)}`
                    : null;
                  const lastLocWeather = lastLocWeatherKey
                    ? weatherCache[lastLocWeatherKey]?.[dayId]
                    : null;

                  const hasTentativeWithCity = (calendarItems || []).some(
                    (e) => e.type === 'tentative' && e.city && dayId >= e.startDate && dayId <= e.endDate
                  );
                  const isTravelDay = locs.length > 1 || hasTentativeWithCity;
                  
                  const isOutsideDateFilter = (mapStartDate && dayId < mapStartDate) || (mapEndDate && dayId > mapEndDate);
                  
                  const dayItems = isMainMonth
                    ? finalItems.filter(
                          (item) => dayId >= item.startDate && dayId <= item.endDate
                        ).map(item => ({ item }))
                    : [];
                  const hiddenCount = Math.max(0, dayItems.length - 4);

                  return (
                    <DroppableDay
                      key={day.toISOString()}
                      day={day}
                      isMainMonth={isMainMonth}
                      isToday={isToday}
                      isPast={isPast}
                      isTravelDay={isTravelDay}
                      isOutsideDateFilter={isOutsideDateFilter}
                      dayId={dayId}
                      monthKey={month.monthKey}
                      visibleSections={visibleSections}
                      isSegmented={isSegmented}
                      dayItems={dayItems}
                      maxSectionItems={maxSectionItems}
                      baseTzToHide={data?.baseTzToHide}
                      profile={profile}
                      hiddenCount={hiddenCount}
                      handleDayWheel={(e) => {
                        const container = e.currentTarget.querySelector('.day-content-area');
                        if (!container) return;
                        
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const y = e.clientY - rect.top;
                        
                        const centerX = rect.width / 2;
                        
                        const isCenter = Math.abs(x - centerX) < (rect.width * 0.5) / 2;
                        
                        if (isCenter) {
                          const isScrollable = container.scrollHeight > container.clientHeight;
                          if (isScrollable) {
                            e.preventDefault();
                            e.stopPropagation();
                            container.scrollTop += e.deltaY;
                          }
                        }
                      }}
                      dispatch={dispatch}
                      navigate={navigate}
                      setSelectedFlight={setSelectedFlight}
                      getItemColor={getItemColor}
                      toggleTask={toggleTask}
                      hoveredEventId={hoveredEventId}
                      setHoveredEventId={setHoveredEventId}
                      fontSize={calendarFontSize}
                      cellHeight={calendarCellHeight}
                      showTinyWeather={showTinyWeather}
                      showFullWeather={showFullWeather}
                      showDayCounters={showDayCounters}
                      showEventLocations={showEventLocations}
                      now={now}
                      firstLocWeather={firstLocWeather}
                      lastLocWeather={lastLocWeather}
                      firstLocCity={firstLoc?.city}
                      lastLocCity={lastLoc?.city}
                      firstLocTzAbbr={firstLocTzAbbr}
                      collapsedHeaderIds={collapsedHeaderIds}
                      isCompact={isCompact}
                      isCalendarBold={isCalendarBold}
                      visibleCalendars={visibleCalendars}
                      habits={habits}
                      habitLogs={habitLogs}
                      handleSaveHabit={handleSaveHabit}
                      handleLogHabit={handleLogHabit}
                      authUser={authUser}
                      mapStartDate={mapStartDate}
                      mapEndDate={mapEndDate}
                      setMapStartDate={setMapStartDate}
                      setMapEndDate={setMapEndDate}
                      setSelectedTripId={setSelectedTripId}
                      dragStartRef={dragStartRef}
                      customCenter={customCenter}
                      searchRadius={searchRadius}
                      songkickConcerts={songkickConcerts}
                    />
                  );
                })}
              </div>
              );
            })()}
            </div>
          );
        })}
      </div>
    </div>
  );
});

MonthRenderer.displayName = 'MonthRenderer';

const CalendarView = memo(
  ({
    tasks,
    personalTasks,
    currentView,
    dispatch,
    toggleTask,
    googleEvents,
    googleCalendars = [],
    visibleCalendars,
    navigate,
    events,
    calendarItems,
    getItemColor,
    calendarFontSize,
    calendarCellHeight,
    collapsedHeaderIds,
    profile,
    allUsers,
    saveUserProfile,
    advancedFilters = [],
    isFilterPanelOpen = false,
    isMapPanelOpen = false,
    habits = [],
    habitLogs = [],
    handleSaveHabit,
    handleLogHabit,
    handleRemoveFlight,
    authUser,
    mapStartDate: propMapStartDate,
    mapEndDate: propMapEndDate,
    searchRadius: propSearchRadius,
    customCenter: propCustomCenter,
    friendFilter: propFriendFilter,
    allTasks = [],

    selectedTripId: propSelectedTripId,
    isDatabasePanelOpen = false,
  }) => {
    const calendarScrollRef = useRef(null);
    const isNavigatingRef = useRef(false);
    const [calendarLabelDate, setCalendarLabelDate] = useState(new Date());
    const [hoveredEventId, setHoveredEventId] = useState(null);
    const [overlayPlaceId, setOverlayPlaceId] = useState(null);
    const [overlayConcert, setOverlayConcert] = useState(null);
    const [now, setNow] = useState(new Date());
    const [tinyWeatherPref, setTinyWeatherPref] = useState(() => {
      const saved = localStorage.getItem('nytm-calendar-tiny-weather');
      return saved ? JSON.parse(saved) : true;
    });
    const [showFullWeather, setShowFullWeather] = useState(() => {
      const saved = localStorage.getItem('nytm-calendar-full-weather');
      return saved ? JSON.parse(saved) : false;
    });

    const [showDayCounters, setShowDayCounters] = useState(false);
    const { recommendations, loading: recsLoading } = useRecommendations();
    const { songkickConcerts } = useProject();
    const [collapsedSections, setCollapsedSections] = useState({
      'nytm-tasks': false,
      'nytm-events': false
    });
    const { tagsHierarchy, availableTags, getParentTags, getChildTags } = useTags();

    const recsWithCat = useMemo(() => {
      return (recommendations || []).map(rec => {
        const isFood = rec.tags?.some(t => getParentTags(t).includes('Food') || t === 'Food');
        const isDrinks = rec.tags?.some(t => getParentTags(t).includes('Drinks') || t === 'Drinks' || getParentTags(t).includes('Drink') || t === 'Drink');
        const isCoffee = rec.tags?.some(t => getParentTags(t).includes('Coffee') || t === 'Coffee');
        const isSight = rec.tags?.some(t => getParentTags(t).includes('Sights') || t === 'Sights' || getParentTags(t).includes('Sight') || t === 'Sight');
        const isShopping = rec.tags?.some(t => getParentTags(t).includes('Shopping') || t === 'Shopping');
        const isMusicVenue = rec.tags?.some(t => getParentTags(t).includes('Music Venue') || t === 'Music Venue');
        const isWantToGo = rec.tags?.some(t => typeof t === 'string' && t.toLowerCase() === 'want to go');
        return { ...rec, _cat: { isFood, isDrinks, isCoffee, isSight, isShopping, isMusicVenue, isWantToGo } };
      });
    }, [recommendations, getParentTags]);

    const selectedTags = useMemo(() => advancedFilters === null ? [] : advancedFilters.filter(f => f.startsWith('tag-')).map(f => f.replace('tag-', '')), [advancedFilters]);
    const tagsByRoot = useMemo(() => ({
      'restaurant': selectedTags.filter(t => getParentTags(t).includes('Food') || t === 'Food'),
      'drinks': selectedTags.filter(t => getParentTags(t).includes('Drinks') || t === 'Drinks' || getParentTags(t).includes('Drink') || t === 'Drink'),
      'coffee': selectedTags.filter(t => getParentTags(t).includes('Coffee') || t === 'Coffee'),
      'sight': selectedTags.filter(t => getParentTags(t).includes('Sights') || t === 'Sights' || getParentTags(t).includes('Sight') || t === 'Sight'),
      'shopping': selectedTags.filter(t => getParentTags(t).includes('Shopping') || t === 'Shopping'),
      'musicvenue': selectedTags.filter(t => getParentTags(t).includes('Music Venue') || t === 'Music Venue')
    }), [selectedTags, getParentTags]);

    useEffect(() => {
      if (advancedFilters === null && Object.keys(tagsHierarchy).length > 0) {
        const allTags = [];
        Object.keys(tagsHierarchy).forEach(root => {
          allTags.push(`tag-${root}`);
          allTags.push(...getChildTags(root).map(c => `tag-${c}`));
        });
        dispatch({ type: 'SET_ADVANCED_FILTERS', payload: allTags });
      }
    }, [advancedFilters, tagsHierarchy, getChildTags, dispatch]);



    useEffect(() => {
      const handleOpenPlaceDetails = (e) => {
        if (!isMapPanelOpen && e.detail && e.detail.placeId) {
          setOverlayPlaceId(e.detail.placeId);
        }
      };
      const handleOpenConcertDetails = (e) => {
        if (!isMapPanelOpen && e.detail && e.detail.concert) {
          setOverlayConcert(e.detail.concert);
        }
      };
      window.addEventListener('OPEN_PLACE_DETAILS', handleOpenPlaceDetails);
      window.addEventListener('OPEN_CONCERT_DETAILS', handleOpenConcertDetails);
      return () => {
        window.removeEventListener('OPEN_PLACE_DETAILS', handleOpenPlaceDetails);
        window.removeEventListener('OPEN_CONCERT_DETAILS', handleOpenConcertDetails);
      };
    }, [isMapPanelOpen]);

    const [showEventLocations, setShowEventLocations] = usePersistentState('nytm-calendar-event-locations', false);
    
    const [isSegmented, setIsSegmented] = usePersistentState('nytm-calendar-segmented', true);
    const [isCompact, setIsCompact] = usePersistentState('nytm-calendar-compact', false);
    const [isCalendarBold, setIsCalendarBold] = usePersistentState('nytm-calendar-bold', true);

    const showTinyWeather = tinyWeatherPref && !showFullWeather;
    
    const [isTravelStripHidden, setIsTravelStripHidden] = usePersistentState('nytm-calendar-travel-strip-hidden', false);
    const [weatherCache, setWeatherCache] = useState(() => {
      try {
        const saved = sessionStorage.getItem('nytm-weather-cache-v2');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Date.now() - parsed.timestamp < 30 * 60 * 1000) {
            return parsed.data;
          }
        }
      } catch(e) {}
      return {};
    });
    
    useEffect(() => {
      if (Object.keys(weatherCache).length > 0) {
        sessionStorage.setItem('nytm-weather-cache-v2', JSON.stringify({
          timestamp: Date.now(),
          data: weatherCache
        }));
      }
    }, [weatherCache]);

    const [tempCoordsCache, setTempCoordsCache] = useState({});
    const getTodayStr = () => new Date(new Date().getTime() - (new Date().getTimezoneOffset() * 60000)).toISOString().split('T')[0];
    
    const mapStartDate = propMapStartDate ?? getTodayStr();
    const mapEndDate = propMapEndDate ?? '';
    const searchRadius = propSearchRadius ?? 50;
    const customCenter = propCustomCenter ?? null;
    const friendFilter = propFriendFilter ?? 'all';
    const selectedTripId = propSelectedTripId ?? '1month';

    const setMapStartDate = useCallback((val) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { mapStartDate: typeof val === 'function' ? val(mapStartDate) : val } }), [dispatch, mapStartDate]);
    const setMapEndDate = useCallback((val) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { mapEndDate: typeof val === 'function' ? val(mapEndDate) : val } }), [dispatch, mapEndDate]);
    const setSearchRadius = useCallback((val) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { searchRadius: typeof val === 'function' ? val(searchRadius) : val } }), [dispatch, searchRadius]);
    const setCustomCenter = useCallback((val) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { customCenter: typeof val === 'function' ? val(customCenter) : val } }), [dispatch, customCenter]);
    const setFriendFilter = useCallback((val) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { friendFilter: typeof val === 'function' ? val(friendFilter) : val } }), [dispatch, friendFilter]);
    const setSelectedTripId = useCallback((val) => dispatch({ type: 'SET_MAP_FILTER_STATE', payload: { selectedTripId: typeof val === 'function' ? val(selectedTripId) : val } }), [dispatch, selectedTripId]);

    const dragStartRef = useRef(null);
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [openTagPopup, setOpenTagPopup] = useState(null);

    const trips = useMemo(() => {
      return (calendarItems || [])
        .filter((item) => item.type === 'trip')
        .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
    }, [calendarItems]);

    const getFutureDateStr = (monthsAhead) => {
      const d = new Date();
      d.setMonth(d.getMonth() + monthsAhead);
      return new Date(d.getTime() - (d.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
    };

    const prevTripIdForDates = useRef(null);
    useEffect(() => {
      if (['1month', '2months', '3months', 'all'].includes(selectedTripId)) {
        if (prevTripIdForDates.current !== selectedTripId) {
          setMapStartDate(getTodayStr());
          
          if (selectedTripId === '2months') setMapEndDate(getFutureDateStr(2));
          else if (selectedTripId === '3months') setMapEndDate(getFutureDateStr(3));
          else setMapEndDate(getFutureDateStr(1)); // 1 month default
          
          prevTripIdForDates.current = selectedTripId;
        }
        return;
      }
      
      const trip = trips.find((item) => item.id === selectedTripId);
      if (trip && prevTripIdForDates.current !== selectedTripId) {
        setMapStartDate(trip.startDate || getTodayStr());
        setMapEndDate(trip.endDate || getTodayStr());
        prevTripIdForDates.current = selectedTripId;
      }
    }, [selectedTripId, trips]);

    const lastScrollPos = useRef(null);
    const requestedWeatherRef = useRef(new Set());
    const requestedGeocodeRef = useRef(new Set());

    useEffect(() => {
      const timer = setInterval(() => setNow(new Date()), 60000);
      return () => clearInterval(timer);
    }, []);

    useEffect(() => {
      const handleAddTentative = async (e) => {
        const item = e.detail;
        const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
        const { db, auth } = await import('../firebase');
        const { appId: APP_ID_CONSTANT } = await import('../constants');
        
        const todayObj = new Date();
        const todayStr = new Date(todayObj.getTime() - (todayObj.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
        
        const city = item.city || '';
        const docRef = await safeAddDoc(collection(db, 'artifacts', APP_ID_CONSTANT, 'public', 'data', 'events'), {
          type: 'tentative',
          title: item.title,
          startDate: item.startDate || todayStr,
          endDate: item.endDate || todayStr,
          isAllDay: true,
          createdAt: serverTimestamp(),
          createdBy: auth.currentUser?.uid || 'test-uid-123',
          city: city,
          placeId: item.placeId || null,
          lat: item.lat || null,
          lng: item.lng || null
        });
        
        dispatch({ 
          type: 'OPEN_EDIT_EVENT', 
          payload: { 
            id: docRef.id, 
            type: 'tentative', 
            title: item.title,
            startDate: item.startDate || todayStr,
            endDate: item.endDate || todayStr,
            venueName: item.venueName || item.title || '',
            address: item.address || '',
            addressLat: item.lat || null,
            addressLng: item.lng || null,
            city: city
          } 
        });
      };
      
      window.addEventListener('ADD_TENTATIVE_FROM_MAP', handleAddTentative);
      
      const handleOpenEdit = (e) => {
        const item = e.detail;
        dispatch({ 
          type: 'OPEN_EDIT_EVENT', 
          payload: { 
            id: item.id,
            type: item.type,
            title: item.title,
            startDate: item.startDate,
            endDate: item.endDate,
            venueName: item.venueName || '',
            address: item.address || '',
            addressLat: item.lat || null,
            addressLng: item.lng || null,
            city: item.city || ''
          } 
        });
      };
      window.addEventListener('OPEN_EDIT_EVENT_FROM_MAP', handleOpenEdit);

      return () => {
        window.removeEventListener('ADD_TENTATIVE_FROM_MAP', handleAddTentative);
        window.removeEventListener('OPEN_EDIT_EVENT_FROM_MAP', handleOpenEdit);
      };
    }, []);

    // Weather Fetching Effect
    useEffect(() => {
      if (!profile?.cities) return;

      const timer = setTimeout(() => {
        runWeatherLogic();
      }, 2000); // 2 second delay to let everything settle

      const runWeatherLogic = async () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const windowEnd = new Date(today);
        windowEnd.setDate(today.getDate() + 16);
        
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(today.getDate() - 90);
        
        const limitStart = new Date('2020-01-01T00:00:00');

        const forecastRequests = new Map(); // key -> {lat, lng}
        const archiveRequests = new Map(); // key -> {lat, lng, ranges: [{start, end}]}
        const normalize = (val) => (val !== null && val !== undefined) ? Number(val).toFixed(2) : null;

        const locations = [];
        
        // Home city
        const homeCity = profile?.cities?.[0];
        if (homeCity?.name) {
          locations.push({ 
            name: homeCity.name, 
            lat: homeCity.lat, 
            lng: homeCity.lng, 
            start: ninetyDaysAgo.toISOString().split('T')[0], 
            end: windowEnd.toISOString().split('T')[0] 
          });
        }

        // Trip cities
        (calendarItems || []).forEach(item => {
          if (item.type === 'trip' && item.city) {
            locations.push({ 
              name: item.city, 
              lat: item.lat, 
              lng: item.lng, 
              start: item.startDate, 
              end: item.endDate 
            });
          }
        });

        // 1. Geocode missing cities
        const missing = locations.filter(l => (!l.lat || !l.lng) && !tempCoordsCache[l.name] && !requestedGeocodeRef.current.has(l.name));
        if (missing.length > 0) {
          if (import.meta.env.DEV) console.log('[DEBUG] Geocoding missing cities:', missing.map(m => m.name));
          const updates = {};
          for (const loc of missing) {
            try {
              requestedGeocodeRef.current.add(loc.name);
              let found = false;
              let searchTerms = [loc.name];
              if (loc.name.includes(',')) {
                 const parts = loc.name.split(',').map(p => p.trim());
                 // Reverse the parts so we check "TX" then "Dallas", which is more likely to match a city than the street address
                 searchTerms = [loc.name, ...parts.reverse()];
              }
              
              for (const term of searchTerms) {
                if (!term || term.match(/^\d/)) continue; // skip strings starting with digits (street addresses, zip codes)
                const geoUrl = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(term)}&count=1&language=en&format=json`;
                const geoRes = await fetch(geoUrl);
                const geoData = await geoRes.json();
                if (geoData.results?.[0]) {
                  const res = geoData.results[0];
                  updates[loc.name] = { lat: res.latitude, lng: res.longitude };
                  found = true;
                  break;
                }
              }
              
              if (!found) {
                console.warn(`[Weather] Geocode completely failed for ${loc.name}, skipping future attempts`);
                updates[loc.name] = { lat: null, lng: null }; // Prevent infinite retries
              }
            } catch (e) {
              console.error(`[Weather] Geocode failed for ${loc.name}`, e);
            }
          }
          if (Object.keys(updates).length > 0) {
            if (import.meta.env.DEV) console.log('[DEBUG] Updating tempCoordsCache with:', updates);
            setTempCoordsCache(prev => ({ ...prev, ...updates }));
            return; 
          }
        }

        // 2. Build fetch maps
        for (const loc of locations) {
          const lat = loc.lat || tempCoordsCache[loc.name]?.lat;
          const lng = loc.lng || tempCoordsCache[loc.name]?.lng;
          if (!lat || !lng) continue;

          const key = `${normalize(lat)},${normalize(lng)}`;
          const start = new Date(loc.start + 'T12:00:00');
          const end = new Date(loc.end + 'T12:00:00');

          if (end >= ninetyDaysAgo) {
            forecastRequests.set(key, { lat, lng });
          }

          if (start < ninetyDaysAgo && end >= limitStart) {
            const effectiveStart = start < limitStart ? '2020-01-01' : loc.start;
            const archiveEnd = end < ninetyDaysAgo ? loc.end : ninetyDaysAgo.toISOString().split('T')[0];
            
            if (!archiveRequests.has(key)) {
              archiveRequests.set(key, { lat, lng, ranges: [] });
            }
            archiveRequests.get(key).ranges.push({ start: effectiveStart, end: archiveEnd });
          }
        }

        // Sequential fetches to avoid hitting rate limits too hard
        for (const [key, { lat, lng }] of forecastRequests.entries()) {
          const cacheKey = key + '_f';
          if (weatherCache[cacheKey] || requestedWeatherRef.current.has(cacheKey)) continue;
          
          try {
            requestedWeatherRef.current.add(cacheKey);
            // Main Weather
            const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&daily=temperature_2m_max,temperature_2m_min,apparent_temperature_max,apparent_temperature_min,sunrise,sunset,wind_direction_10m_dominant,daylight_duration,weather_code,precipitation_probability_max,precipitation_sum,snowfall_sum,precipitation_hours,wind_speed_10m_max,uv_index_max&timezone=auto&past_days=92&forecast_days=16&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch`;
            const res = await fetch(url);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            
            // Air Quality
            let aqiData = null;
            try {
              const aqiUrl = `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lng}&hourly=us_aqi&timezone=auto`;
              const aqiRes = await fetch(aqiUrl);
              aqiData = await aqiRes.json();
            } catch (err) {
              console.error('[Weather] AQI fetch failed', key, err);
            }

            if (data.daily) {
              const dailyMap = {};
              data.daily.time.forEach((date, i) => {
                let maxAqi = null;
                if (aqiData?.hourly?.us_aqi) {
                  const dayStart = `${date}T00:00`;
                  const dayEnd = `${date}T23:00`;
                  const dayIndices = aqiData.hourly.time.reduce((acc, t, idx) => {
                    if (t >= dayStart && t <= dayEnd) acc.push(idx);
                    return acc;
                  }, []);
                  if (dayIndices.length > 0) {
                    const dayAqis = dayIndices.map(idx => aqiData.hourly.us_aqi[idx]).filter(v => v !== null);
                    if (dayAqis.length > 0) maxAqi = Math.max(...dayAqis);
                  }
                }

                dailyMap[date] = {
                  max: Math.round(data.daily.temperature_2m_max[i]),
                  min: Math.round(data.daily.temperature_2m_min[i]),
                  apparentMax: Math.round(data.daily.apparent_temperature_max[i]),
                  apparentMin: Math.round(data.daily.apparent_temperature_min[i]),
                  sunrise: data.daily.sunrise[i],
                  sunset: data.daily.sunset[i],
                  windDir: data.daily.wind_direction_10m_dominant[i],
                  daylightDuration: data.daily.daylight_duration[i],
                  code: data.daily.weather_code[i],
                  precip: data.daily.precipitation_probability_max[i],
                  precipSum: data.daily.precipitation_sum[i],
                  snowSum: data.daily.snowfall_sum[i],
                  precipHours: data.daily.precipitation_hours[i],
                  wind: Math.round(data.daily.wind_speed_10m_max[i]),
                  uv: data.daily.uv_index_max[i],
                  aqi: maxAqi,
                };
              });
              setWeatherCache(prev => ({ 
                ...prev, 
                [key]: { ...(prev[key] || {}), ...dailyMap },
                [cacheKey]: true 
              }));
            }
            await new Promise(r => setTimeout(r, 500));
          } catch (e) {
            console.error('[Weather] Forecast failed', key, e);
          }
        }

        // 4. Fetch Archive Sequentially
        for (const [key, { lat, lng, ranges }] of archiveRequests.entries()) {
          for (const range of ranges) {
            const rangeKey = `${key}_${range.start}_${range.end}`;
            if (weatherCache[rangeKey] || requestedWeatherRef.current.has(rangeKey)) continue;
            try {
              requestedWeatherRef.current.add(rangeKey);
              const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&start_date=${range.start}&end_date=${range.end}&daily=temperature_2m_max,temperature_2m_min,apparent_temperature_max,apparent_temperature_min,sunrise,sunset,wind_direction_10m_dominant,daylight_duration,weather_code&timezone=auto&temperature_unit=fahrenheit`;
              const res = await fetch(url);
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const data = await res.json();
              if (data.daily) {
                const dailyMap = {};
                data.daily.time.forEach((date, i) => {
                  dailyMap[date] = {
                    max: Math.round(data.daily.temperature_2m_max[i]),
                    min: Math.round(data.daily.temperature_2m_min[i]),
                    apparentMax: Math.round(data.daily.apparent_temperature_max[i]),
                    apparentMin: Math.round(data.daily.apparent_temperature_min[i]),
                    sunrise: data.daily.sunrise[i],
                    sunset: data.daily.sunset[i],
                    windDir: data.daily.wind_direction_10m_dominant[i],
                    daylightDuration: data.daily.daylight_duration[i],
                    code: data.daily.weather_code[i],
                  };
                });
                setWeatherCache(prev => ({ 
                  ...prev, 
                  [key]: { ...(prev[key] || {}), ...dailyMap },
                  [rangeKey]: true 
                }));
              }
              await new Promise(r => setTimeout(r, 500));
            } catch (e) {
              console.error('[Weather] Archive failed', rangeKey, e);
            }
          }
        }
      };

      return () => clearTimeout(timer);
    }, [profile, calendarItems, tempCoordsCache]);

    useLayoutEffect(() => {
      if (lastScrollPos.current && calendarScrollRef.current) {
        const { weekStart, offset } = lastScrollPos.current;
        const container = calendarScrollRef.current;
        const targetWeek = container.querySelector(`[data-week-start="${weekStart}"]`);

        if (targetWeek) {
          const originalScrollBehavior = container.style.scrollBehavior;
          container.style.scrollBehavior = 'auto';
          container.scrollTop = targetWeek.offsetTop - offset;
          container.style.scrollBehavior = originalScrollBehavior;
        }
        lastScrollPos.current = null;
      }
    }, [calendarCellHeight]);

    const handleSetCellHeight = (newHeight) => {
      const container = calendarScrollRef.current;
      if (container) {
        const weeks = container.querySelectorAll('.week-container');
        let topWeek = null;
        for (const week of weeks) {
          if (week.offsetTop + week.offsetHeight > container.scrollTop + 21) {
            topWeek = week;
            break;
          }
        }
        if (topWeek) {
          lastScrollPos.current = {
            weekStart: topWeek.getAttribute('data-week-start'),
            offset: topWeek.offsetTop - container.scrollTop,
          };
        }
      }
      dispatch({ type: 'SET_CALENDAR_CELL_HEIGHT', payload: newHeight });
    };

    const mockMarkers = useMemo(() => {
      const markers = [];
      const filters = advancedFilters || [];
      if (filters.includes('malaysian')) {
        markers.push(
          { id: 'm1', lat: 3.1390, lng: 101.6869, title: 'OldTown White Coffee', description: 'Classic Malaysian breakfast spot', category: 'Malaysian' },
          { id: 'm2', lat: 3.1478, lng: 101.7102, title: 'Madam Kwan\'s', description: 'Truly Malaysian Cuisine', category: 'Malaysian' }
        );
      }
      if (filters.includes('coffee')) {
        markers.push(
          { id: 'c1', lat: 37.7749, lng: -122.4194, title: 'Blue Bottle Coffee', description: 'Specialty coffee roaster', category: 'Coffee' },
          { id: 'c2', lat: 40.7128, lng: -74.0060, title: 'Stumptown Coffee', description: 'Ace Hotel location', category: 'Coffee' }
        );
      }
      if (filters.includes('bars')) {
        markers.push(
          { id: 'b1', lat: 51.5074, lng: -0.1278, title: 'The Churchill Arms', description: 'Famous floral pub', category: 'Bars' },
          { id: 'b2', lat: 48.8566, lng: 2.3522, title: 'Harry\'s New York Bar', description: 'Classic cocktails', category: 'Bars' }
        );
      }
      return markers;
    }, [advancedFilters]);

    const handleToggleAdvancedFilter = (id) => {
      let newList = [...advancedFilters];
      if (id.startsWith('tag-')) {
        const rawTag = id.replace('tag-', '');
        if (advancedFilters.includes(id)) {
          newList = newList.filter(f => f !== id);
          const children = getChildTags(rawTag);
          newList = newList.filter(f => !children.includes(f.replace('tag-', '')));
          const parents = getParentTags(rawTag);
          if (parents) {
            parents.forEach(p => {
              newList = newList.filter(f => f !== `tag-${p}`);
            });
          }
        } else {
          newList.push(id);
          // Automatically check all child tags when a parent is checked
          const children = getChildTags(rawTag);
          if (children) {
            children.forEach(c => {
              const cid = `tag-${c}`;
              if (!newList.includes(cid)) newList.push(cid);
            });
          }
          // Do not automatically check parent tags when checking a child in the filter.
          // Otherwise, checking 'Pizza' would check 'Food' and cause all 'Food' to show up.
        }
      } else {
        if (advancedFilters.includes(id)) {
          newList = newList.filter(f => f !== id);
        } else {
          newList.push(id);
        }
      }
      dispatch({ type: 'SET_ADVANCED_FILTERS', payload: newList });
    };

    const handleToggleAllTags = (selectAll) => {
      if (selectAll) {
        const allTags = availableTags.map(t => `tag-${t}`);
        const nonTagFilters = advancedFilters.filter(f => !f.startsWith('tag-'));
        dispatch({ type: 'SET_ADVANCED_FILTERS', payload: [...nonTagFilters, ...allTags] });
      } else {
        const nonTagFilters = advancedFilters.filter(f => !f.startsWith('tag-'));
        dispatch({ type: 'SET_ADVANCED_FILTERS', payload: nonTagFilters });
      }
    };

    const calendarMonths = useMemo(() => {
      const today = new Date();
      
      const jan2015 = new Date('2015-01-01T00:00:00');
      const diff = (today.getFullYear() - jan2015.getFullYear()) * 12 + today.getMonth() - jan2015.getMonth();
      const monthsBack = diff > 0 ? diff : 0;
      const monthsForward = 48; // default to 4 years forward
      
      return getCalendarMonths(today, monthsBack, monthsForward, profile?.mondayFirst || false);
    }, [profile?.mondayFirst]);

    const priorityMonthKeys = useMemo(() => {
      const today = new Date();
      const keys = [];
      for (let i = -2; i <= 6; i++) {
        const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
        keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
      }
      return keys;
    }, []);

    const todayMapLocation = useMemo(() => {
      const nowLoc = new Date();
      const todayStr = `${nowLoc.getFullYear()}-${String(nowLoc.getMonth() + 1).padStart(2, '0')}-${String(nowLoc.getDate()).padStart(2, '0')}`;
      
      const todayTrips = (calendarItems || []).filter(item => 
        item.type === 'trip' && 
        item.startDate <= todayStr && 
        item.endDate >= todayStr
      );

      const outTrips = todayTrips.filter(t => t.endDate === todayStr && t.startDate !== todayStr);
      const stayTrips = todayTrips.filter(t => t.startDate < todayStr && t.endDate > todayStr);
      const inTrips = todayTrips.filter(t => t.startDate === todayStr);

      const homeCity = profile?.cities?.[0] || { name: 'No Location Set', lat: 37.7749, lng: -122.4194 };
      
      const locs = [];
      outTrips.forEach(t => locs.push(t));
      stayTrips.forEach(t => locs.push(t));
      if (stayTrips.length === 0) {
        locs.push(homeCity);
      }
      inTrips.forEach(t => locs.push(t));

      const activeLoc = locs[locs.length - 1];
      
      // If activeLoc is a trip but is missing coordinates, check if tempCoordsCache has them
      let latVal = activeLoc?.lat;
      let lngVal = activeLoc?.lng;
      
      if (!latVal && activeLoc?.city && tempCoordsCache[activeLoc.city]) {
        latVal = tempCoordsCache[activeLoc.city].lat;
        lngVal = tempCoordsCache[activeLoc.city].lng;
      }

      const finalLat = parseFloat(latVal) || parseFloat(homeCity.lat) || 37.7749;
      const finalLng = parseFloat(lngVal) || parseFloat(homeCity.lng) || -122.4194;
      const finalName = activeLoc?.city || activeLoc?.name || homeCity.name || "Current Location";

      return {
        lat: finalLat,
        lng: finalLng,
        name: finalName
      };
    }, [calendarItems, profile, tempCoordsCache]);

    const scrollToMonth = (monthKey) => {
      if (!calendarScrollRef.current) return;
      const targetFirstDayId = `${monthKey}-01`;
      
      const weeks = calendarScrollRef.current.querySelectorAll(`[data-week-start]`);
      let targetEl = null;
      for (let el of weeks) {
        const start = el.getAttribute('data-week-start');
        const end = el.getAttribute('data-week-end');
        if (targetFirstDayId >= start && targetFirstDayId <= end) {
          targetEl = el;
          break;
        }
      }

      if (targetEl) {
        calendarScrollRef.current.scrollTo({ top: targetEl.offsetTop - 20, behavior: 'smooth' });
        const [y, m] = monthKey.split('-').map(Number);
        setCalendarLabelDate(new Date(y, m - 1, 1));
      } else {
        const monthEl = calendarScrollRef.current.querySelector(`[data-month-key="${monthKey}"]`);
        if (monthEl) {
          calendarScrollRef.current.scrollTo({ top: monthEl.offsetTop, behavior: 'smooth' });
          const [y, m] = monthKey.split('-').map(Number);
          setCalendarLabelDate(new Date(y, m - 1, 1));
        }
      }
    };

    const scrollToToday = useCallback(() => {
      if (!calendarScrollRef.current) return;
      const today = new Date();
      const todayId = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      if (import.meta.env.DEV) console.log('[Calendar] scrollToToday called. todayId:', todayId);

      const weeks = calendarScrollRef.current.querySelectorAll(`[data-week-start]`);
      if (import.meta.env.DEV) console.log('[Calendar] Found weeks count:', weeks.length);

      let targetEl = null;
      for (let el of weeks) {
        const start = el.getAttribute('data-week-start');
        const end = el.getAttribute('data-week-end');
        if (todayId >= start && todayId <= end) {
          targetEl = el;
          break;
        }
      }

      if (targetEl) {
        if (import.meta.env.DEV) console.log('[Calendar] Target week found:', targetEl.getAttribute('data-week-start'));
        calendarScrollRef.current.scrollTo({ top: targetEl.offsetTop - 20, behavior: 'smooth' });
        setCalendarLabelDate(today);
      } else {
        if (import.meta.env.DEV) console.log('[Calendar] Target week NOT found, falling back to month.');
        const todayMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
        const monthEl = calendarScrollRef.current.querySelector(
          `[data-month-key="${todayMonthKey}"]`
        );
        if (monthEl) {
          calendarScrollRef.current.scrollTo({ top: monthEl.offsetTop, behavior: 'smooth' });
          setCalendarLabelDate(today);
        }
      }
    }, []);

    useEffect(() => {
      const timer = setTimeout(scrollToToday, 200);
      return () => clearTimeout(timer);
    }, [currentView, scrollToToday]);

    const handleCalendarScroll = (e) => {
      if (isNavigatingRef.current) return;
      const container = e.target;
      const viewportCenter = container.scrollTop + container.clientHeight / 2;
      const monthContainers = container.querySelectorAll('[data-month-key]');
      let activeMonthKey = null;

      for (const month of monthContainers) {
        if (month.offsetTop <= viewportCenter) {
          activeMonthKey = month.getAttribute('data-month-key');
        } else {
          break;
        }
      }

      if (activeMonthKey) {
        const [year, month] = activeMonthKey.split('-').map(Number);
        const newDate = new Date(year, month - 1, 1);
        if (
          newDate.getMonth() !== calendarLabelDate.getMonth() ||
          newDate.getFullYear() !== calendarLabelDate.getFullYear()
        ) {
          setCalendarLabelDate(newDate);
        }
      }
    };

    const handleGoToToday = () => {
      isNavigatingRef.current = true;
      scrollToToday();
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 500);
    };

    const navigateMonth = (direction) => {
      if (!calendarScrollRef.current) return;
      isNavigatingRef.current = true;
      const targetDate = new Date(
        calendarLabelDate.getFullYear(),
        calendarLabelDate.getMonth() + direction,
        1
      );
      
      const targetMonthKey = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`;
      const targetFirstDayId = `${targetMonthKey}-01`;
      
      const weeks = calendarScrollRef.current.querySelectorAll(`[data-week-start]`);
      let targetEl = null;
      for (let el of weeks) {
        const start = el.getAttribute('data-week-start');
        const end = el.getAttribute('data-week-end');
        if (targetFirstDayId >= start && targetFirstDayId <= end) {
          targetEl = el;
          break;
        }
      }

      if (targetEl) {
        calendarScrollRef.current.scrollTo({ top: targetEl.offsetTop - 20, behavior: 'smooth' });
        setCalendarLabelDate(targetDate);
      } else {
        const monthEl = calendarScrollRef.current.querySelector(
          `[data-month-key="${targetMonthKey}"]`
        );
        if (monthEl) {
          calendarScrollRef.current.scrollTo({ top: monthEl.offsetTop, behavior: 'smooth' });
          setCalendarLabelDate(targetDate);
        }
      }

      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 500);
    };

    const navigateYear = (direction) => {
      if (!calendarScrollRef.current) return;
      isNavigatingRef.current = true;
      
      const container = calendarScrollRef.current;
      const viewportTop = container.scrollTop;
      
      const currentMonthKey = `${calendarLabelDate.getFullYear()}-${String(calendarLabelDate.getMonth() + 1).padStart(2, '0')}`;
      const currentMonthEl = container.querySelector(`[data-month-key="${currentMonthKey}"]`);
      
      let targetWeekIndex = 0;
      let offsetWithinWeek = 0;
      
      if (currentMonthEl) {
        const weeks = Array.from(currentMonthEl.querySelectorAll('.week-container'));
        let minDistance = Infinity;
        
        weeks.forEach((week, index) => {
          // Check which week is closest to the top of the viewport
          const distance = Math.abs(week.offsetTop - viewportTop);
          if (distance < minDistance) {
            minDistance = distance;
            targetWeekIndex = index;
            offsetWithinWeek = viewportTop - week.offsetTop;
          }
        });
      }

      const targetDate = new Date(
        calendarLabelDate.getFullYear() + direction,
        calendarLabelDate.getMonth(),
        1
      );
      const targetMonthKey = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`;
      const targetMonthEl = container.querySelector(`[data-month-key="${targetMonthKey}"]`);
      
      if (targetMonthEl) {
        const targetWeeks = targetMonthEl.querySelectorAll('.week-container');
        // Find the matching week index or the closest one available
        const targetWeek = targetWeeks[Math.min(targetWeekIndex, Math.max(0, targetWeeks.length - 1))];
        
        if (targetWeek) {
          container.scrollTo({ top: targetWeek.offsetTop + offsetWithinWeek, behavior: 'smooth' });
        } else {
          container.scrollTo({ top: targetMonthEl.offsetTop, behavior: 'smooth' });
        }
        setCalendarLabelDate(targetDate);
      }
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 500);
    };

    const handleDayWheel = (e) => {
      const container = e.currentTarget.querySelector('.day-content-area');
      if (!container) return;
      
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const isCenter = Math.abs(x - centerX) < (rect.width * 0.5) / 2 && Math.abs(y - centerY) < (rect.height * 0.5) / 2;
      
      if (isCenter) {
        const isScrollable = container.scrollHeight > container.clientHeight;
        if (isScrollable) {
          e.preventDefault();
          e.stopPropagation();
          container.scrollTop += e.deltaY;
        }
      }
    };

    
    const [visibleSections, setVisibleSections] = usePersistentState('nytm.calendar.filters.visibleSections', ['group-google', 'group-events', 'group-travel', 'group-tasks', 'group-recommendations', 'group-other']);
    const visibleList = Array.isArray(visibleCalendars)
      ? visibleCalendars
      : [
          'nytm-event',
          'nytm-tasks-all',
          'nytm-tasks-starred',
          'nytm-tasks-h',
          'nytm-tasks-m',
          'nytm-tasks-l',
          'nytm-birthday',
          'nytm-holiday',
        ];

    const toggleCalendar = (id) => {
      let newList = [...visibleList];
      if (newList.includes(id)) {
        newList = newList.filter((x) => x !== id);
      } else {
        newList.push(id);
      }
      dispatch({ type: 'SET_CALENDAR_VISIBILITY', payload: newList });
    };

    useEffect(() => {
      if (
        !visibleCalendars ||
        visibleCalendars.length === 0 ||
        visibleCalendars.includes('nytm-tasks')
      ) {
        const initial = [
          'nytm-event',
          'nytm-tasks-all',
          'nytm-tasks-starred',
          'nytm-tasks-h',
          'nytm-tasks-m',
          'nytm-tasks-l',
          'nytm-birthday',
          'nytm-holiday',
        ];
        dispatch({ type: 'SET_CALENDAR_VISIBILITY', payload: initial });
      }
    }, []);

    return (
      <div className="flex-1 flex flex-col bg-white overflow-hidden h-full relative">
        <header className="border-b border-zinc-100 flex flex-col flex-shrink-0 bg-zinc-50/30">
          <div className="h-8 px-4 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-1">
                <button
                  onClick={handleGoToToday}
                  className="px-3 py-1 text-[10px] font-black text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200 rounded border border-zinc-300 transition-colors uppercase tracking-widest shadow-sm"
                >
                  Today
                </button>
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-1 text-zinc-400 hover:text-zinc-900 rounded hover:bg-zinc-200 transition-colors"
                >
                  <ChevronLeft size={18} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-1 text-zinc-400 hover:text-zinc-900 rounded hover:bg-zinc-200 transition-colors"
                >
                  <ChevronRight size={18} strokeWidth={2.5} />
                </button>
              </div>
              <div
                className="flex items-center gap-1.5 font-bold text-xs text-zinc-800 uppercase tracking-wider w-[170px]"
              >
                <span data-testid="calendar-header-month" className="truncate">
                  {calendarLabelDate.toLocaleDateString(undefined, { month: 'long' })}
                </span>
                <div className="flex items-center gap-0.5 bg-zinc-100/50 rounded-sm px-1 border border-zinc-200/50">
                  <button
                    onClick={() => navigateYear(-1)}
                    className="w-5 h-5 flex items-center justify-center text-xs text-zinc-400 hover:text-zinc-900 rounded hover:bg-zinc-200 transition-colors"
                  >
                    -
                  </button>
                  <span className="tabular-nums px-0.5">{calendarLabelDate.getFullYear()}</span>
                  <button
                    onClick={() => navigateYear(1)}
                    className="w-5 h-5 flex items-center justify-center text-xs text-zinc-400 hover:text-zinc-900 rounded hover:bg-zinc-200 transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 border-l border-zinc-200 pl-4">
                <button
                  onClick={() => dispatch({ type: 'TOGGLE_MAP_PANEL' })}
                  className={`p-1 rounded transition-colors ${isMapPanelOpen ? 'bg-zinc-900 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200'}`}
                  title="World Map"
                >
                  <MapIcon size={16} strokeWidth={2.5} />
                </button>
                {auth?.currentUser?.email === 'brett.gaddy@gmail.com' && (
                  <button
                    onClick={() => dispatch({ type: 'TOGGLE_DATABASE_PANEL' })}
                    className={`p-1 rounded transition-colors ${isDatabasePanelOpen ? 'bg-zinc-900 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200'}`}
                    title="Database"
                  >
                    <Database size={16} strokeWidth={2.5} />
                  </button>
                )}

                <div className="w-px h-4 bg-zinc-200 mx-1" />

                {[
                  { id: 'group-google', items: [
                    { id: 'nytm-google-calendar', label: 'Google Calendar', color: 'bg-emerald-500', textColor: 'text-emerald-500', isCheckbox: true, icon: Calendar }
                  ]},
                  { id: 'group-events', items: [
                    { id: 'nytm-event', label: 'Events', color: 'bg-blue-500', isCheckbox: true, icon: Calendar },
                    { id: 'nytm-tentative', label: 'Tentative', color: 'bg-indigo-500', isCheckbox: true, icon: HelpCircle }
                  ]},
                  { id: 'group-travel', items: [
                    { id: 'nytm-flight', label: 'Flights', color: 'bg-sky-500', isCheckbox: true, icon: Plane },
                    { id: 'nytm-hotel', label: 'Hotels', color: 'bg-indigo-500', isCheckbox: true, icon: Bed },
                    { id: 'nytm-birthday', label: 'Birthdays', color: 'bg-pink-500', textColor: 'text-pink-500', isCheckbox: true, icon: Cake },
                    { id: 'nytm-holiday', label: 'Holidays', color: 'bg-violet-600', textColor: 'text-violet-600', isCheckbox: true, icon: PartyPopper }
                  ]},
                  { id: 'group-tasks', items: [
                    { id: 'nytm-tasks-prj', label: 'Project Tasks', color: 'bg-zinc-800', textColor: 'text-zinc-800', isCheckbox: false, textIcon: 'PRJ', isPriority: false },
                    { id: 'nytm-tasks-h', label: 'High Priority Tasks', color: 'bg-green-600', textColor: 'text-green-600', isCheckbox: false, icon: ListTodo, isPriority: false },
                    { id: 'nytm-tasks-m', label: 'Medium Priority Tasks', color: 'bg-green-400', textColor: 'text-green-400', isCheckbox: false, icon: ListTodo, isPriority: false },
                    { id: 'nytm-tasks-l', label: 'Low Priority Tasks', color: 'bg-green-200', textColor: 'text-green-200', isCheckbox: false, icon: ListTodo, isPriority: false }
                  ]},
                  { id: 'group-recommendations', items: [
                    { id: 'nytm-wanttogo', label: 'Want to Go', color: 'bg-emerald-500', textColor: 'text-emerald-500', isCheckbox: false, icon: BookmarkPlus, extraClass: 'mr-2' },
                    { id: 'nytm-restaurant', label: 'Food', color: 'bg-orange-500', textColor: 'text-orange-500', isCheckbox: false, icon: Utensils },
                    { id: 'nytm-drinks', label: 'Drinks', color: 'bg-purple-500', textColor: 'text-purple-500', isCheckbox: false, icon: Martini },
                    { id: 'nytm-coffee', label: 'Coffee', color: 'bg-amber-700', textColor: 'text-amber-700', isCheckbox: false, icon: Coffee },
                    { id: 'nytm-sight', label: 'Sights', color: 'bg-teal-500', textColor: 'text-teal-500', isCheckbox: false, icon: Binoculars },
                    { id: 'nytm-shopping', label: 'Shopping', color: 'bg-rose-500', textColor: 'text-rose-500', isCheckbox: false, icon: ShoppingBag },
                    { id: 'nytm-musicvenue', label: 'Music Venues', color: 'bg-indigo-500', textColor: 'text-indigo-500', isCheckbox: false, icon: Music },
                    { id: 'nytm-concert', label: 'Concerts', color: 'bg-pink-500', textColor: 'text-pink-500', isCheckbox: false, icon: Ticket, extraClass: 'ml-2' }
                  ]},
                  { id: 'group-other', items: [
                    { id: 'nytm-habit', label: 'Habits', color: 'bg-zinc-900', isCheckbox: true, icon: Repeat }
                  ]}
                ].map((group) => {
                  const isSectionVisible = group.items.length === 1 ? true : visibleSections.includes(group.id);
                  return (
                    <div key={group.id} className="relative flex items-center group/section bg-zinc-50 border border-zinc-200 rounded-lg p-0.5 ml-1">
                      {group.items.length > 1 && (
                        <button
                          onClick={() => {
                             setVisibleSections(prev => 
                                prev.includes(group.id) ? prev.filter(id => id !== group.id) : [...prev, group.id]
                             );
                          }}
                          className={`relative inline-flex h-3 w-5 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ml-1 mr-0.5 ${isSectionVisible ? 'bg-zinc-800' : 'bg-zinc-300'}`}
                          role="switch"
                          aria-checked={isSectionVisible}
                          title={`Toggle ${group.id.replace('group-', '')} section`}
                        >
                          <span
                            aria-hidden="true"
                            className={`pointer-events-none inline-block h-2 w-2 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isSectionVisible ? 'translate-x-2' : 'translate-x-0'}`}
                          />
                        </button>
                      )}
                      <div className={`flex items-center gap-1.5 px-1 transition-opacity duration-300 ${isSectionVisible ? 'opacity-100' : 'opacity-30 grayscale'}`}>
                        {group.items.map((cal) => {
                  const isSelected = visibleList.includes(cal.id);
                  const isDisabled = false;
                  const Icon = cal.icon;
                  
                  const idToTopTag = {
                    'nytm-restaurant': 'Food',
                    'nytm-drinks': 'Drink',
                    'nytm-coffee': 'Coffee',
                    'nytm-sight': 'Sight',
                    'nytm-shopping': 'Shopping',
                    'nytm-musicvenue': 'Music Venue'
                  };
                  const topTag = idToTopTag[cal.id];
                  
                  const allTagsForTop = topTag && tagsHierarchy && tagsHierarchy[topTag] ? [`tag-${topTag}`, ...getChildTags(topTag).map(c => `tag-${c}`)] : [];
                  const activeCount = advancedFilters === null ? allTagsForTop.length : allTagsForTop.filter(t => advancedFilters.includes(t)).length;
                  const isFiltered = activeCount < allTagsForTop.length;
                  
                  return (
                    <div key={cal.id} className="relative flex items-center">
                      <button
                        onContextMenu={(e) => {
                          e.preventDefault();
                          if (cal.id === 'nytm-google-calendar') {
                            setOpenTagPopup(openTagPopup === cal.id ? null : cal.id);
                          } else if (topTag) {
                            setOpenTagPopup(openTagPopup === cal.id ? null : cal.id);
                          }
                        }}
                        onClick={(e) => {
                          if (!isDisabled && isSectionVisible) {
                            toggleCalendar(cal.id);
                          }
                        }}
                        disabled={isDisabled || !isSectionVisible}
                        className={`flex items-center gap-1.5 group transition-opacity select-none ${isDisabled || !isSectionVisible ? 'cursor-not-allowed opacity-30 grayscale' : 'cursor-pointer'} ${cal.extraClass || ''}`}
                        title={cal.label + ((topTag || cal.id === 'nytm-google-calendar') ? " (Right-click for options)" : "")}
                      >
                        {(!Icon && !cal.textIcon || cal.isPriority) && (
                          <div
                            className={`w-3.5 h-3.5 flex items-center justify-center transition-all ${cal.isCheckbox ? 'rounded-sm' : 'rounded-full'} border ${isSelected ? cal.color + ' border-transparent' : 'border-zinc-300 bg-white'}`}
                          >
                            {isSelected &&
                              (cal.isCheckbox ? (
                                <Check size={10} strokeWidth={4} className="text-white" />
                              ) : (
                                <div className="w-1.5 h-1.5 rounded-full bg-white" />
                              ))}
                          </div>
                        )}
                        {Icon && (
                          <div className="relative flex items-center justify-center">
                            <Icon 
                              size={16} 
                              strokeWidth={isSelected ? 2.5 : 2} 
                              className={`transition-colors ${isSelected ? (cal.textColor || cal.color.replace('bg-', 'text-')) : 'text-zinc-400 group-hover:text-zinc-600'}`} 
                            />
                            {isFiltered && (
                              <div className="absolute -top-1.5 -right-2 px-1 text-[8px] leading-none font-bold bg-blue-500 text-white rounded-full border-2 border-white shadow-sm flex items-center justify-center" title="Partially filtered">
                                {activeCount}/{allTagsForTop.length}
                              </div>
                            )}
                          </div>
                        )}
                        {cal.textIcon && (
                          <div className={`text-[11px] font-black tracking-tight px-1 transition-colors ${isSelected ? 'text-zinc-900' : 'text-zinc-400 group-hover:text-zinc-600'}`}>
                            {cal.textIcon}
                          </div>
                        )}
                      </button>
                      
                      {openTagPopup === cal.id && topTag && (
                        <>
                          <div className="fixed inset-0 z-[90]" onClick={() => setOpenTagPopup(null)} />
                          <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-white border border-zinc-200 rounded-xl shadow-2xl p-3 z-[100] w-48 max-h-64 overflow-y-auto">
                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-zinc-100">
                              <div className="flex items-center gap-3">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const children = getChildTags(topTag);
                                    const tagsToAdd = [`tag-${topTag}`, ...children.map(c => `tag-${c}`)];
                                    const newList = Array.from(new Set([...(advancedFilters || []), ...tagsToAdd]));
                                    dispatch({ type: 'SET_ADVANCED_FILTERS', payload: newList });
                                  }}
                                  className="text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-zinc-900 transition-colors"
                                >
                                  Select All
                                </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const children = getChildTags(topTag);
                                    const tagsToRemove = [`tag-${topTag}`, ...children.map(c => `tag-${c}`)];
                                    const newList = (advancedFilters || []).filter(f => !tagsToRemove.includes(f));
                                    dispatch({ type: 'SET_ADVANCED_FILTERS', payload: newList });
                                  }}
                                  className="text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-zinc-900 transition-colors"
                                >
                                  Clear All
                                </button>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); setOpenTagPopup(null); }} className="text-zinc-400 hover:text-zinc-800 transition-colors">
                                <X size={14} />
                              </button>
                            </div>
                            {Object.keys(tagsHierarchy[topTag] || {}).length > 0 ? (
                              <div className="flex flex-col gap-1">
                                {Object.entries(tagsHierarchy[topTag] || {}).map(([childTag, grandChildren]) => (
                                  <TagNode 
                                    key={childTag} 
                                    tag={childTag} 
                                    childrenObj={grandChildren} 
                                    activeFilters={advancedFilters} 
                                    onToggleFilter={handleToggleAdvancedFilter} 
                                  />
                                ))}
                              </div>
                            ) : (
                              <div className="text-[10px] text-zinc-500 italic px-1">No sub-filters available.</div>
                            )}
                            <div className="mt-3 pt-2 border-t border-zinc-100 flex justify-end">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenTagPopup(null);
                                }}
                                className="bg-zinc-900 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
                              >
                                OK
                              </button>
                            </div>
                          </div>
                        </>
                      )}

                      {openTagPopup === cal.id && cal.id === 'nytm-google-calendar' && (
                        <>
                          <div className="fixed inset-0 z-[90]" onClick={() => setOpenTagPopup(null)} />
                          <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-white border border-zinc-200 rounded-xl shadow-2xl p-3 z-[100] w-64 max-h-80 overflow-y-auto">
                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-zinc-100">
                              <span className="text-xs font-bold text-zinc-800">Google Calendars</span>
                              <button onClick={(e) => { e.stopPropagation(); setOpenTagPopup(null); }} className="text-zinc-400 hover:text-zinc-800 transition-colors">
                                <X size={14} />
                              </button>
                            </div>
                            {googleCalendars && googleCalendars.length > 0 ? (
                              <div className="flex flex-col gap-2">
                                {googleCalendars.map(gcal => {
                                  // Find the selected status: either explicitly saved in profile, or default to gcal.selected
                                  const isChecked = profile?.googleCalendarSelectedIds 
                                    ? profile.googleCalendarSelectedIds.includes(gcal.id)
                                    : gcal.selected;
                                    
                                  return (
                                    <div key={gcal.id} className="flex items-center gap-2 group cursor-pointer" onClick={async (e) => {
                                      e.stopPropagation();
                                      const currentSelected = profile?.googleCalendarSelectedIds || googleCalendars.filter(c => c.selected).map(c => c.id);
                                      let newSelected;
                                      if (currentSelected.includes(gcal.id)) {
                                        newSelected = currentSelected.filter(id => id !== gcal.id);
                                      } else {
                                        newSelected = [...currentSelected, gcal.id];
                                      }
                                      if (saveUserProfile) {
                                        await saveUserProfile({ googleCalendarSelectedIds: newSelected });
                                      }
                                    }}>
                                      <div 
                                        className={`w-3 h-3 rounded-sm border flex items-center justify-center transition-colors ${isChecked ? 'border-transparent' : 'border-zinc-300 bg-white'}`}
                                        style={{ backgroundColor: isChecked ? gcal.backgroundColor : undefined }}
                                      >
                                        {isChecked && <Check size={8} strokeWidth={4} className="text-white" />}
                                      </div>
                                      <span className="text-xs text-zinc-600 font-medium truncate flex-1 group-hover:text-zinc-900 transition-colors">
                                        {gcal.summary}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-[10px] text-zinc-500 italic px-1">No calendars found.</div>
                            )}
                            <div className="mt-3 pt-2 border-t border-zinc-100 flex justify-end">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenTagPopup(null);
                                }}
                                className="bg-zinc-900 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
                              >
                                OK
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>
            );
          })}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* Weather Toggles (Pinned left of controls) */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => {
                    const newVal = !tinyWeatherPref;
                    setTinyWeatherPref(newVal);
                    localStorage.setItem('nytm-calendar-tiny-weather', JSON.stringify(newVal));
                  }}
                  className="p-1 rounded transition-colors text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200"
                  title="Tiny Weather"
                >
                  <CloudSun size={16} strokeWidth={showTinyWeather ? 2.5 : 2} className={`transition-colors ${showTinyWeather ? 'text-zinc-800' : ''}`} />
                </button>

                <button
                  onClick={() => {
                    const val = !showFullWeather;
                    setShowFullWeather(val);
                    localStorage.setItem('nytm-calendar-full-weather', JSON.stringify(val));
                  }}
                  className={`p-1 rounded transition-colors ${showFullWeather ? 'bg-zinc-900 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200'}`}
                  title="Full Weather Mode"
                >
                  <Radar size={14} strokeWidth={2.5} />
                </button>

                <button
                  onClick={() => {
                    const next = !showEventLocations;
                    setShowEventLocations(next);
                    localStorage.setItem('nytm-calendar-event-locations', JSON.stringify(next));
                  }}
                  className={`p-1 rounded transition-colors ${showEventLocations ? 'bg-zinc-900 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200'}`}
                  title="Toggle Event Locations"
                >
                  <MapPin size={14} strokeWidth={2.5} />
                </button>
                <button
                  onClick={() => {
                    const next = !isCalendarBold;
                    setIsCalendarBold(next);
                    localStorage.setItem('nytm-calendar-bold', JSON.stringify(next));
                  }}
                  className={`w-6 h-6 flex items-center justify-center rounded font-serif italic font-black transition-all ${isCalendarBold ? 'bg-zinc-900 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200'}`}
                  title="Toggle Bold Text"
                >
                  B
                </button>
              </div>

              {/* Font Size & Height Controls */}
              <div className="flex items-center gap-2 border-r border-zinc-200 pr-4">
                <div className="flex items-center bg-zinc-100 rounded-lg p-0.5 shadow-inner">
                  <button
                    onClick={() =>
                      dispatch({
                        type: 'SET_CALENDAR_FONT_SIZE',
                        payload: Math.max(6, calendarFontSize - 1),
                      })
                    }
                    className="w-6 h-6 flex items-center justify-center rounded text-zinc-400 hover:text-zinc-900 hover:bg-white transition-all font-black text-xs"
                    title="Decrease Text Size"
                  >
                    -
                  </button>
                  <div className="w-8 flex items-center justify-center text-[10px] font-black text-zinc-600 tabular-nums">
                    {calendarFontSize}
                  </div>
                  <button
                    onClick={() =>
                      dispatch({
                        type: 'SET_CALENDAR_FONT_SIZE',
                        payload: Math.min(12, calendarFontSize + 1),
                      })
                    }
                    className="w-6 h-6 flex items-center justify-center rounded text-zinc-400 hover:text-zinc-900 hover:bg-white transition-all font-black text-xs"
                    title="Increase Text Size"
                  >
                    +
                  </button>
                </div>
                <div className="flex items-center bg-zinc-100 rounded-lg p-0.5 shadow-inner ml-1">
                  {[
                    { id: 'small', label: 'S' },
                    { id: 'medium', label: 'M' },
                    { id: 'large', label: 'L' },
                    { id: 'xl', label: 'XL' },
                  ].map((h) => (
                    <button
                      key={h.id}
                      onClick={() => handleSetCellHeight(h.id)}
                      className={`w-6 h-6 flex items-center justify-center rounded text-[10px] font-black transition-all ${calendarCellHeight === h.id ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-600'}`}
                    >
                      {h.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center bg-zinc-100 rounded-lg p-0.5 shadow-inner ml-1">
                  <button
                    onClick={() => {
                      const newVal = !isSegmented;
                      setIsSegmented(newVal);
                      localStorage.setItem('nytm-calendar-segmented', JSON.stringify(newVal));
                    }}
                    className={`w-6 h-6 flex items-center justify-center rounded transition-all mr-0.5 ${!isSegmented ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-600'}`}
                    title={isSegmented ? "Switch to Fluid View" : "Switch to Segmented View"}
                  >
                    {isSegmented ? <Menu size={12} strokeWidth={2.5} /> : <AlignJustify size={12} strokeWidth={2.5} />}
                  </button>
                  <button
                    onClick={() => {
                      const newVal = !isCompact;
                      setIsCompact(newVal);
                      localStorage.setItem('nytm-calendar-compact', JSON.stringify(newVal));
                    }}
                    className={`w-6 h-6 flex items-center justify-center rounded transition-all ${isCompact ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-600'}`}
                    title={isCompact ? "Switch to Standard View" : "Switch to Compact View"}
                  >
                    {isCompact ? <Rows size={12} strokeWidth={2.5} /> : <Grid3x3 size={12} strokeWidth={2.5} />}
                  </button>
                </div>
                {/* Travel Strip Toggle (Right of compact view) */}
                <button
                  onClick={() => {
                    const next = !isTravelStripHidden;
                    setIsTravelStripHidden(next);
                    localStorage.setItem('nytm-calendar-travel-strip-hidden', JSON.stringify(next));
                  }}
                  className={`p-1 rounded transition-colors ml-1 ${!isTravelStripHidden ? 'bg-zinc-900 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-900 hover:bg-zinc-200'}`}
                  title={isTravelStripHidden ? 'Show Travel Strip' : 'Hide Travel Strip'}
                >
                  <PlaneTakeoff size={16} strokeWidth={2.5} />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  data-testid="calendar-add-btn"
                  onClick={() =>
                    dispatch({
                      type: 'OPEN_ADD_EVENT',
                      payload: new Date().toISOString().split('T')[0],
                    })
                  }
                  className="p-1.5 hover:bg-zinc-900/15 rounded text-zinc-500 hover:text-zinc-900 transition-colors flex-shrink-0"
                >
                  <Plus size={22} strokeWidth={3} />
                </button>
              </div>
            </div>
          </div>
          <div className="w-full h-5 border-t border-zinc-200 bg-zinc-50 flex-shrink-0 overflow-x-auto custom-scrollbar-hidden calendar-weekday-header">
            <div className="min-w-[1100px] grid grid-cols-7 h-full">
              {(profile?.mondayFirst 
                ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] 
                : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
              ).map((d) => (
                <div
                  key={d}
                  className="flex items-center justify-center text-[10px] font-black text-zinc-900 uppercase tracking-widest border-r border-zinc-200 last:border-r-0"
                >
                  {d}
                </div>
              ))}
            </div>
          </div>
        </header>



        <div
          ref={calendarScrollRef}
          className="flex-1 overflow-y-auto custom-scrollbar bg-zinc-200"
          style={{ display: (isFilterPanelOpen && isMapPanelOpen) ? 'none' : 'block' }}
          onScroll={(e) => {
            handleCalendarScroll(e);
            const header = document.querySelector('.calendar-weekday-header');
            if (header) header.scrollLeft = e.target.scrollLeft;
          }}
        >
          <div className="min-w-[1100px] relative min-h-full">
            <div className="flex flex-col bg-zinc-200">
              {calendarMonths.map((month) => (
                <MonthRenderer
                  key={month.monthKey}
                  month={month}
                  calendarItems={calendarItems}
                  profile={profile}
                  calendarFontSize={calendarFontSize}
                  calendarCellHeight={calendarCellHeight}
                  dispatch={dispatch}
                  navigate={navigate}
                  setSelectedFlight={setSelectedFlight}
                  getItemColor={getItemColor}
                  toggleTask={toggleTask}
                  hoveredEventId={hoveredEventId}
                  setHoveredEventId={setHoveredEventId}
                  collapsedHeaderIds={collapsedHeaderIds}
                  now={now}
                  weatherCache={weatherCache}
                  isPriority={priorityMonthKeys.includes(month.monthKey)}
                  showTinyWeather={showTinyWeather}
                  showFullWeather={showFullWeather}
                  showDayCounters={showDayCounters}
                  showEventLocations={showEventLocations}
                  isSegmented={isSegmented}
                  isCompact={isCompact}

                  isTravelStripHidden={isTravelStripHidden}
                  advancedFilters={advancedFilters}
                  visibleCalendars={visibleList}
                  visibleSections={visibleSections}
                  habits={habits}
                  habitLogs={habitLogs}
                  handleSaveHabit={handleSaveHabit}
                  recsWithCat={recsWithCat}
                  selectedTags={selectedTags}
                  tagsByRoot={tagsByRoot}
                  friendFilter={friendFilter}
                  handleLogHabit={handleLogHabit}
                  authUser={authUser}
                  mapStartDate={mapStartDate}
                  mapEndDate={mapEndDate}
                  setMapStartDate={setMapStartDate}
                  setMapEndDate={setMapEndDate}
                  setSelectedTripId={setSelectedTripId}
                  dragStartRef={dragStartRef}
                  tempCoordsCache={tempCoordsCache}
                  tagsHierarchy={tagsHierarchy}
                  availableTags={availableTags}
                  getParentTags={getParentTags}
                  getChildTags={getChildTags}
                  songkickConcerts={songkickConcerts}
                  searchRadius={searchRadius}
                  customCenter={customCenter}
                  isCalendarBold={isCalendarBold}
                />
              ))}
            </div>
          </div>
        </div>

        {isMapPanelOpen && (
          <CalendarMapPanel
            isOpen={isMapPanelOpen}
            calendarItems={calendarItems}
            activeFilters={advancedFilters}
            visibleCalendars={visibleCalendars}
            profile={profile}
            onClose={() => dispatch({ type: 'TOGGLE_MAP_PANEL' })}
            dispatch={dispatch}
            lat={todayMapLocation.lat}
            lng={todayMapLocation.lng}
            locationName={todayMapLocation.name}
            mapStartDate={mapStartDate}
            mapEndDate={mapEndDate}
            selectedTripId={selectedTripId}
            trips={trips}
            recommendations={recommendations}
            friendFilter={friendFilter}
            allUsers={allUsers}
            authUser={authUser}
            searchRadius={searchRadius}
            customCenter={customCenter}
            tagsHierarchy={tagsHierarchy}
            availableTags={availableTags}
            getParentTags={getParentTags}
            getChildTags={getChildTags}
          />
        )}

        {isDatabasePanelOpen && (
          <DatabaseView
            isOpen={isDatabasePanelOpen}
            calendarItems={calendarItems}
            activeFilters={advancedFilters}
            visibleCalendars={visibleCalendars}
            profile={profile}
            dispatch={dispatch}
            lat={todayMapLocation.lat}
            lng={todayMapLocation.lng}
            locationName={todayMapLocation.name}
            mapStartDate={mapStartDate}
            mapEndDate={mapEndDate}
            selectedTripId={selectedTripId}
            trips={trips}
            recommendations={recommendations}
            friendFilter={friendFilter}
            allUsers={allUsers}
            authUser={authUser}
            searchRadius={searchRadius}
            customCenter={customCenter}
            tagsHierarchy={tagsHierarchy}
            availableTags={availableTags}
            getParentTags={getParentTags}
            getChildTags={getChildTags}
            allTasks={allTasks}
            habits={habits}
          />
        )}

        <FlightDetailModal
          flight={selectedFlight}
          onClose={() => setSelectedFlight(null)}
          onEditTrip={(trip) => dispatch({ type: 'OPEN_EDIT_EVENT', payload: trip })}
          onRemoveFlight={handleRemoveFlight}
        />

        {overlayPlaceId && (
          <MapSearchBox
            overlayMode={true}
            externalPlaceId={overlayPlaceId}
            onClearSearch={() => setOverlayPlaceId(null)}
            recommendations={recommendations}
            authUser={authUser}
          />
        )}

        {overlayConcert && (
          <ConcertDetailsOverlay
            concert={overlayConcert}
            onClose={() => setOverlayConcert(null)}
            dispatch={dispatch}
          />
        )}
      </div>
    );
  }
);

CalendarView.displayName = 'CalendarView';

export default CalendarView;
